import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import ControlBar from "./ControlBar";
import { RaisedHandsProvider } from "./RaisedHands";
import ConnectingView from "./ConnectingView";
import ParticipantComponent from "./ParticipantComponent";
import SpeakingTranslatorLiveKit from "./SpeakingTranslatorLiveKit";
import CallHeader from "./CallHeader";
import Participants from "./Participants";
import { useWebSocketDataService } from "../../Context/WebsocketContext";
import {
  AudioTrack,
  useLocalParticipant,
  useMediaDeviceSelect,
  useRemoteParticipants,
  useRoomContext,
  useTracks,
  useSpeakingParticipants,
} from "@livekit/components-react";
import { useKrispNoiseFilter } from '@livekit/components-react/krisp';

import { Howl } from "howler";
import callingSound from "../../Assets/sounds/ring.mp3";
import connectedSound from "../../Assets/sounds/connected.mp3";
import beepSound from "../../Assets/sounds/beep.wav";
import ParticipantsWrapper from "./ParticipantsWrapper";


import {
  createLocalVideoTrack,
  LocalVideoTrack,
  LocalAudioTrack,
  ParticipantEvent, RoomEvent,
  Track,
  TrackEvent,
  VideoPresets
} from "livekit-client";


import SmallOwnCameraComponent from "./SmallOwnCameraComponent";
import { useAuthService } from "../../Context/AuthContext";
import ParticipantsWrapperTranslationGrid from "./ParticipantsWrapperTranslationGrid";

import { useTranslate } from '@tolgee/react';
import ScreenSharingGrid from "./ScreenSharingGrid";
import ShareScreenPicker from "../../electron/ShareScreenPicker";
import { startAppAudioTrack } from "../../electron/appAudioCapture";
import { ReactComponent as ForwardArrowIcon } from "../../Assets/iconsv2/generic/forwardArrow.svg";
import { ReactComponent as BackArrowIcon } from "../../Assets/iconsv2/generic/backArrow.svg";
import { BackgroundBlur, VirtualBackground } from "@livekit/track-processors";
import CallOptionModal from "./CallOptionModal";
import {
  BLUR_BG,
  MORE_BLUR_BG,
  DISABLED_TRANSLATION,
  IMAGE_BG,
  NORMAL_BG,
  CONFERENCE_ROLE_ATTENDEE,
  CONFERENCE_ROLE_ORGANIZER,
  CONFERENCE_ROLE_PRESENTER,
  INCOMING_VOICE_MAX_GAIN,
  VOICE_VOLUME_50,
} from "../../Constants/constantValues";
import { normalizeConferenceLanguageKey } from "../../Helpers/callHelper/conferenceLanguageKey";

import LocalStorage from "../../Helpers/LocalStorage";
import { useMeetingsService } from "../../Context/MeetingContext";
import { useCallsService } from "../../Context/CallsContext";
import { useNotify } from "../../Helpers/Notifications";
import CallChatPanel from "../CallChatComponents/CallChatPanel";
import PollPanel from "../Conferences/PollPanel";
import { useConversationService } from "../../Context/ConversationsContext";
import { checkCallChatConversation } from "../../API/calls/callChatService";
import { endConferenceApiCall, getConferenceCallDetailsApiCall, toggleConferenceChatApiCall } from "../../API/conferences/conferencesService";
import { isMobile } from "react-device-detect";
import { startRecordingApiCall, stopRecordingApiCall, updateMeetingCallSettingsApiCall } from "../../API/calls/callsService";
import { createLogger } from "../../utils/logger";

const avLog = createLogger("conf-audio");

const KRISP_ENABLED = false;
const REMOTE_NOISE_FILTER_ENABLED = true;
const RIGHT_PANEL_OVERLAY_ENABLED = false;
const SCREENSHARE_OVERLAY_AUTOTOGGLE = false;
const SILENT_VOLUME = 0.0001;

const AgentAwareAudioRenderer = ({
  emotiiVoice,
  userId,
  isSpeaking,
  useRealtimeApi,
  useLiveKitAgentAudio,
  nonAgentVolume,
  applyVolumes,
}) => {
  const tracks = useTracks(
    [Track.Source.Microphone, Track.Source.ScreenShareAudio, Track.Source.Unknown],
    { updateOnlyOn: [], onlySubscribed: true }
  ).filter((trackRef) => {
    const { participant, publication } = trackRef;
    if (participant.isLocal) return false;
    if (publication.kind !== Track.Kind.Audio) return false;

    const parts = (participant.identity || "").split("|");
    if (parts.length >= 3 && parts[0] === "agent_emotii") {
      const agentUserId = parts[1];
      if (String(agentUserId) === String(userId)) {
        return (
          (useRealtimeApi || useLiveKitAgentAudio) &&
          emotiiVoice &&
          !isSpeaking
        );
      }
      return false;
    }
    return true;
  });

  const trackSidsKey = tracks
    .map((trackRef) => trackRef.publication?.trackSid)
    .join(",");

  useEffect(() => {
    applyVolumes?.();
  }, [trackSidsKey, applyVolumes]);

  return (
    <div style={{ display: "none" }}>
      {tracks.map((trackRef) => {
        const identity = trackRef.participant.identity || "";
        const isAgentTrack = identity.startsWith("agent_emotii");
        return (
          <AudioTrack
            key={`${trackRef.participant.identity}-${trackRef.publication.trackSid}`}
            trackRef={trackRef}
            volume={
              isAgentTrack
                ? undefined
                : nonAgentVolume <= 0
                ? SILENT_VOLUME
                : nonAgentVolume
            }
          />
        );
      })}
    </div>
  );
};

export const beepSoundHowl = new Howl({
  src: [beepSound],
  loop: false,
});

export const callingSoundHowl = new Howl({
  src: [callingSound],
  loop: true,
});

export const connectedSoundHowl = new Howl({
  src: [connectedSound],
});

const CallView = ({
  data,
  resetCall,
  speechRecongnitionMessages,
  translationEnabled,
  translationType,
  translationEnabledRef,
  handleSendCancelSpeakMessage,
  setLoading,
  setSelectedCall,
  activeCallRef,
  handleRequestSpeakMessage,
  speakersQueue,
  raisedHands = [],
  handleRaiseHand,
  handleLowerHand,
  handleSendTranslationEnabledMessage,
  conferenceTranslationModeChangePending = false,
  setEmotiiVoice,
  emotiiVoice,
  onEmotiiVolumeChange,
  setCurrentSpeakerIsActive,
  callStartTime,
  setCallStartTime,
  useRealtimeApi = false,
  useLiveKitAgentAudio = false,
  realtimeTranslations,
  isRecording,
  setIsRecording,
  realtimeAudioPlaying = false,
  aiSummaryEnabled,
  setAiSummaryEnabled,
  transcriptExportEnabled,
  setTranscriptExportEnabled,
}) => {
  const ONECALL = "single";
  const GROUPCALL = "group";
  const ENDED = "ended";
  const SPEAKING = "speaking";
  const AGENT_IDENTITY_PREFIX = "agent_emotii";
  const isConference = !!data?.conference_id;
  const isPrioritySpeaker =
    isConference &&
    [CONFERENCE_ROLE_ORGANIZER, CONFERENCE_ROLE_PRESENTER].includes(
      data?.conference_role
    );
  const requestSpeakPayload = isPrioritySpeaker
    ? { priority: "high", role: data?.conference_role }
    : null;

  const { user, userRef } = useAuthService();
  const { currentMeetingRef } = useMeetingsService();
  const { t } = useTranslate();
  const conferenceLanguageKey = useMemo(() => {
    return normalizeConferenceLanguageKey(user?.call_language);
  }, [user?.call_language]);
  const conferenceTtsTrackName = useMemo(() => {
    return conferenceLanguageKey ? `agent-tts-${conferenceLanguageKey}` : "";
  }, [conferenceLanguageKey]);
  const canChangeTranslationType = !data?.meeting_id || data?.call_admin_ids?.includes(user?.id);

  const {
    sendCloseCallMessage,
    sendConferenceSpeakerResync,
    sendReorderSpeakersMessage,
    add_observer,
    remove_observer,
  } = useWebSocketDataService();
  const canReorderSpeakers = isConference
    ? data?.conference_role === CONFERENCE_ROLE_ORGANIZER
    : !!data?.call_admin_ids?.includes(user?.id);

  const handleReorderSpeakers = (userIds) => {
    if (!data?.uuid) {
      return;
    }
    sendReorderSpeakersMessage(
      data.uuid,
      userIds,
      isConference ? "ConferenceCallChannel" : "CallChannel"
    );
  };
  const room = useRoomContext();
  const activeSpeakers = useSpeakingParticipants();

  const [incomingVolume, setIncomingVolume] = useState(() => {
    const stored = LocalStorage.getIncomingVoiceVolume(data?.uuid);
    return stored !== null ? stored : VOICE_VOLUME_50;
  });
  
  const [dimEnabled, setDimEnabled] = useState(() => {
    return LocalStorage.getDimVoiceEnabled();
  });
  const [emotiiVolume, setEmotiiVolume] = useState(() => {
    const stored = LocalStorage.getEmotiiVoiceVolume(data?.uuid);
    return stored !== null ? stored : VOICE_VOLUME_50;
  });
  const [conferenceTtsPlaybackRate, setConferenceTtsPlaybackRate] = useState(() => {
    if (!isConference) {
      return 1.0;
    }
    return LocalStorage.getConferenceTtsPlaybackSpeed();
  });
  const isEmotiiAudioEnabled = useMemo(
    () =>
      Boolean(
        isConference ? emotiiVolume > 0 : emotiiVoice && emotiiVolume > 0
      ),
    [emotiiVoice, emotiiVolume, isConference]
  );

  const normalizeConferenceTtsPlaybackRate = (value) => {
    const numericValue = Number(value);
    if (numericValue === 1 || numericValue === 1.25 || numericValue === 1.5) {
      return numericValue;
    }
    return 1.0;
  };

  const handleConferencePlaybackSpeedChange = useCallback((nextPlaybackRate) => {
    const normalizedRate = normalizeConferenceTtsPlaybackRate(nextPlaybackRate);
    setConferenceTtsPlaybackRate(normalizedRate);
    LocalStorage.setConferenceTtsPlaybackSpeed(normalizedRate);
  }, []);
  
  // Conference audio helpers
  const isAudioPublication = (pub) => pub?.kind === "audio";

  const getPublicationTrackName = (pub) => pub?.trackName || "";

  const isConferenceTtsTrackName = (trackName) =>
    typeof trackName === "string" && trackName.startsWith("agent-tts-");

  const isConferenceAgentIdentity = (identity) =>
    typeof identity === "string" &&
    (identity.startsWith("conference-agent|") || identity.startsWith("agent-"));

  const isConferenceLanguageAgentTrack = (identity, trackName) =>
    isConferenceAgentIdentity(identity) && isConferenceTtsTrackName(trackName);

  const conferenceTrackMatchesSelectedLanguage = (_identity, trackName) => {
    if (!conferenceLanguageKey) return false;
    return trackName === `agent-tts-${conferenceLanguageKey}`;
  };

const applyVolumeToTrackElements = (track, safeVolume, isSilent) => {
    const elements = track.attachedElements;
    if (Array.isArray(elements)) {
      elements.forEach((el) => {
        if (!el) return;
        el.volume = safeVolume;
        el.muted = isSilent;
        if (isSilent) {
          if (!el.paused) el.pause();
        } else if (el.paused) {
          el.play().catch(() => {});
        }
      });
    }
    return elements;
  };

  const setPublicationTrackVolume = (publication, volume) => {
    const track = publication?.track;
    if (!track) return;
    const isSilent = volume <= 0;
    const safeVolume = isSilent ? SILENT_VOLUME : volume;
    try {
      if (typeof track.setVolume === "function") track.setVolume(safeVolume);
      const elements = applyVolumeToTrackElements(track, safeVolume, isSilent);

      if (!track.__muteReassertAttached && typeof track.on === "function") {
        track.__muteReassertAttached = true;
        track.on(TrackEvent.ElementAttached, () => {
          const intent = track.__desiredVolumeState;
          if (!intent) return;
          if (typeof track.setVolume === "function") track.setVolume(intent.safeVolume);
          applyVolumeToTrackElements(track, intent.safeVolume, intent.isSilent);
        });
      }
      track.__desiredVolumeState = { safeVolume, isSilent };

      avLog.debug("[CallView] setPublicationTrackVolume applied " + JSON.stringify({
        participantIdentity: publication?.participantIdentity,
        trackSid: publication?.trackSid,
        requestedVolume: volume,
        appliedVolume: safeVolume,
        attachedElementsCount: Array.isArray(elements) ? elements.length : null,
        elementVolumes: Array.isArray(elements) ? elements.map((el) => el?.volume) : null,
        elementMuted: Array.isArray(elements) ? elements.map((el) => el?.muted) : null,
        elementPaused: Array.isArray(elements) ? elements.map((el) => el?.paused) : null,
        reportedGetVolume: typeof track.getVolume === "function" ? track.getVolume() : undefined,
        hasAudioContext: Boolean(track.audioContext),
        hasGainNode: Boolean(track.gainNode),
      }));
    } catch (_err) {}
  };

  const setPublicationTrackPlaybackRate = (publication, rate) => {
    const track = publication?.track;
    if (!track) return;
    try {
      const elements = track.attachedElements;
      if (Array.isArray(elements)) {
        elements.forEach((el) => {
          if (el && typeof el.playbackRate !== "undefined") el.playbackRate = rate;
        });
      }
    } catch (_err) {}
  };

  const applyConferenceTtsPublicationVolume = (participantIdentity, publication) => {
    const trackName = getPublicationTrackName(publication);
    if (!isConferenceLanguageAgentTrack(participantIdentity, trackName)) return;
    const isSelected = conferenceTrackMatchesSelectedLanguage(participantIdentity, trackName);
    const volume = isEmotiiAudioEnabled && isSelected ? emotiiVolume / 100 : 0;
    setPublicationTrackVolume({ ...publication, participantIdentity }, volume);
    if (isSelected) {
      setPublicationTrackPlaybackRate(
        { ...publication, participantIdentity },
        conferenceTtsPlaybackRate
      );
    }
  };

  // Audio playback recovery refs for handling browser autoplay restrictions
  const audioPlaybackCleanupRef = useRef(null);
  const audioPlaybackAttemptingRef = useRef(false);
  const agentSpeakingHeldRef = useRef(false);
  const agentSpeakingHoldTimerRef = useRef(null);
  const conferenceDetailsRefreshInFlightRef = useRef(false);
  const containerRef = useRef(null);
  const remoteParticipant = useRemoteParticipants({
    updateOnlyOn: [
      RoomEvent.ParticipantConnected,
      RoomEvent.ParticipantDisconnected,
      RoomEvent.TrackPublished,
      RoomEvent.TrackUnpublished,
      RoomEvent.TrackSubscribed,
      RoomEvent.TrackUnsubscribed,
      RoomEvent.TrackMuted,
      RoomEvent.TrackUnmuted,
    ],
  });
  const remoteParticipantsRef = useRef(remoteParticipant);
  const hasSeenRemoteParticipantsRef = useRef(false);

  const isLeavingCallRef = useRef(false);

  useEffect(() => {
    remoteParticipantsRef.current = remoteParticipant;
  }, [remoteParticipant]);

  useEffect(() => {
    if (!isConference) {
      return;
    }

    avLog.debug("🔍 [Conference audio debug] Build marker", {
      marker: "conference-audio-v3",
      callUuid: data?.uuid,
      conferenceId: data?.conference_id,
    });
  }, [data?.conference_id, data?.uuid, isConference]);

  const localParticipant = useLocalParticipant();


  const isCurrentSpeaker = useMemo(() => {
    if (!data?.conference_id || !speakersQueue?.length || !user?.id) {
      return false;
    }

    return String(speakersQueue[0]?.user_id) === String(user.id);
  }, [data?.conference_id, speakersQueue, user?.id]);

  const [microphoneStatus, setMicrophoneStatus] = useState({
    publication: null,
    track: null,
    isMuted: true,
    isEnabled: false,
  });

  useEffect(() => {
    const participant = localParticipant.localParticipant;
    if (!participant) {
      setMicrophoneStatus({
        publication: null,
        track: null,
        isMuted: true,
        isEnabled: false,
      });
      return;
    }

    const updateMicrophoneStatus = (publication) => {
      let micPublication = publication;

      if (!micPublication || micPublication?.source !== Track.Source.Microphone) {
        micPublication = participant.getTrackPublication(Track.Source.Microphone);
      }

      if (!micPublication) {
        const audioPublications = Array.from(
          participant.audioTrackPublications?.values?.() || []
        );
        micPublication = audioPublications[0] || null;
      }

      const micTrack = micPublication?.track?.mediaStreamTrack || null;
      const isMuted = micPublication?.track?.isMuted ?? true;
      const isEnabled = participant.isMicrophoneEnabled ?? false;

      setMicrophoneStatus({
        publication: micPublication || null,
        track: micTrack,
        isMuted,
        isEnabled,
      });

      avLog.debug("[CallView] local mic status updated " + JSON.stringify({
        isConference,
        audioPublicationCount: participant.audioTrackPublications?.size,
        micPublicationSource: micPublication?.source,
        trackSid: micPublication?.trackSid,
        hasMediaStreamTrack: Boolean(micTrack),
        isMuted,
        isEnabled,
      }));
    };

    updateMicrophoneStatus();

    participant.on(ParticipantEvent.LocalTrackPublished, updateMicrophoneStatus);
    participant.on(ParticipantEvent.LocalTrackUnpublished, updateMicrophoneStatus);
    participant.on(ParticipantEvent.TrackMuted, updateMicrophoneStatus);
    participant.on(ParticipantEvent.TrackUnmuted, updateMicrophoneStatus);
    participant.on(ParticipantEvent.ParticipantPermissionsChanged, updateMicrophoneStatus);

    return () => {
      participant.off(ParticipantEvent.LocalTrackPublished, updateMicrophoneStatus);
      participant.off(ParticipantEvent.LocalTrackUnpublished, updateMicrophoneStatus);
      participant.off(ParticipantEvent.TrackMuted, updateMicrophoneStatus);
      participant.off(ParticipantEvent.TrackUnmuted, updateMicrophoneStatus);
      participant.off(ParticipantEvent.ParticipantPermissionsChanged, updateMicrophoneStatus);
    };
  }, [localParticipant.localParticipant]);

  const parseAgentUserId = (identity) => {
    if (!identity || typeof identity !== "string") {
      return null;
    }
    const parts = identity.split("|");
    if (parts.length < 3) {
      return null;
    }
    if (parts[0] !== AGENT_IDENTITY_PREFIX) {
      return null;
    }
    return parts[1];
  };

  const shouldSkipConferenceAudioSubscription = (participant) => {
    if (!participant?.identity || typeof participant.identity !== "string") {
      return false;
    }

    return participant.identity.startsWith(AGENT_IDENTITY_PREFIX);
  };

  const forceSubscribeConferenceAudio = (participant, contextLabel) => {
    if (!participant) {
      return false;
    }

    const participantIdentity =
      typeof participant?.identity === "string" ? participant.identity : "";

    const isLegacyAgentIdentity = participantIdentity.startsWith(
      AGENT_IDENTITY_PREFIX
    );

    const publicationsSource =
      participant.trackPublications || participant.tracks || null;

    const publications = publicationsSource
      ? publicationsSource instanceof Map
        ? Array.from(publicationsSource.values())
        : Array.isArray(publicationsSource)
        ? publicationsSource
        : []
      : [];

    const audioPublications = publications.filter((publication) =>
      isAudioPublication(publication)
    );

    if (audioPublications.length > 0) {
      const audioSummary = audioPublications.map((publication) => ({
        trackSid: publication?.trackSid,
        source: publication?.source,
        trackName: getPublicationTrackName(publication),
        isSubscribed: publication?.isSubscribed,
        isMuted: publication?.isMuted,
      }));
      avLog.debug("🔊 [Conference audio] Participant audio publications", {
        context: contextLabel,
        identity: participant?.identity,
        publications: audioSummary,
      });
    }

    let changed = false;

    audioPublications.forEach((publication) => {
      const trackName = getPublicationTrackName(publication);
      const isTtsTrack = isConferenceTtsTrackName(trackName);
      const isTargetTtsTrack =
        Boolean(conferenceTtsTrackName) && trackName === conferenceTtsTrackName;
      const isConferenceLanguageTrack = isConferenceLanguageAgentTrack(
        participantIdentity,
        trackName
      );

      let shouldSubscribe;

      if (isConferenceLanguageTrack) {
        // Volume-only mode for conference TTS: keep selected language subscribed
        // and control audibility via volume.
        shouldSubscribe = conferenceTrackMatchesSelectedLanguage(
          participantIdentity,
          trackName
        );
      } else if (isTtsTrack) {
        shouldSubscribe = Boolean(isTargetTtsTrack);
      } else {
        shouldSubscribe = !isLegacyAgentIdentity;
      }

      avLog.debug("🔍 [Conference audio debug] Subscription decision", {
        context: contextLabel,
        callUuid: data?.uuid,
        conferenceId: data?.conference_id,
        identity: participantIdentity,
        trackSid: publication?.trackSid,
        source: publication?.source,
        trackName,
        isSubscribed: publication?.isSubscribed,
        isLegacyAgentIdentity,
        isConferenceLanguageTrack,
        isTtsTrack,
        isTargetTtsTrack,
        isEmotiiAudioEnabled,
        emotiiVoice,
        emotiiVolume,
        shouldSubscribe,
      });

      if (isConferenceLanguageTrack) {
        avLog.debug("🔍 [Conference audio debug] Conference TTS volume-only mode", {
          context: contextLabel,
          identity: participantIdentity,
          trackSid: publication?.trackSid,
          trackName,
          shouldSubscribe,
          isEmotiiAudioEnabled,
          emotiiVolume,
        });
      }

      if (shouldSubscribe && publication.isSubscribed === false) {
        if (typeof publication.setSubscribed !== "function") {
          avLog.debug("🔊 [Conference audio] Publication missing setSubscribed", {
            context: contextLabel,
            identity: participant?.identity,
            trackSid: publication?.trackSid,
          });
          return;
        }

        try {
          avLog.debug("🔊 [Conference audio] Forcing audio subscribe", {
            context: contextLabel,
            identity: participant?.identity,
            trackSid: publication?.trackSid,
            source: publication?.source,
            trackName,
          });
          publication.setSubscribed(true);
          changed = true;
        } catch (err) {
          avLog.debug("🔊 [Conference audio] Failed to force subscribe", {
            context: contextLabel,
            identity: participant?.identity,
            trackSid: publication?.trackSid,
            error: err,
          });
        }
      }

      if (!shouldSubscribe && publication.isSubscribed !== false) {
        if (typeof publication.setSubscribed !== "function") {
          avLog.debug("🔊 [Conference audio] Publication missing setSubscribed", {
            context: contextLabel,
            identity: participant?.identity,
            trackSid: publication?.trackSid,
          });
          return;
        }

        try {
          avLog.debug("🔊 [Conference audio] Forcing audio unsubscribe", {
            context: contextLabel,
            identity: participant?.identity,
            trackSid: publication?.trackSid,
            source: publication?.source,
            trackName,
          });
          publication.setSubscribed(false);
          setTimeout(() => {
            avLog.debug("🔍 [Conference audio debug] Post-unsubscribe check", {
              context: contextLabel,
              identity: participant?.identity,
              trackSid: publication?.trackSid,
              isSubscribed: publication?.isSubscribed,
            });
          }, 100);
          changed = true;
        } catch (err) {
          avLog.debug("🔊 [Conference audio] Failed to force unsubscribe", {
            context: contextLabel,
            identity: participant?.identity,
            trackSid: publication?.trackSid,
            error: err,
          });
        }
      }

      if (isConferenceLanguageTrack) {
        applyConferenceTtsPublicationVolume(
          participantIdentity,
          publication,
          `${contextLabel}-volume-only`
        );
      }
    });

    return changed;
  };

  const forceSubscribeConferenceAudioSweep = (participants, contextLabel) => {
    if (!Array.isArray(participants)) {
      return;
    }

    let forcedCount = 0;
    participants.forEach((participant) => {
      if (forceSubscribeConferenceAudio(participant, contextLabel)) {
        forcedCount += 1;
      }
    });

    avLog.debug("🔊 [Conference audio] Sweep completed", {
      context: contextLabel,
      totalParticipants: participants.length,
      forcedCount,
    });
  };

  useEffect(() => {
    if (!isConference) {
      return;
    }

    forceSubscribeConferenceAudioSweep(
      remoteParticipant,
      "tts-language-change"
    );
  }, [
    conferenceTtsTrackName,
    isConference,
    isEmotiiAudioEnabled,
    remoteParticipant,
  ]);

  const clearAudioPlaybackListeners = useCallback(() => {
    if (audioPlaybackCleanupRef.current) {
      audioPlaybackCleanupRef.current();
      audioPlaybackCleanupRef.current = null;
    }
  }, []);

  const armConferenceAudioPlayback = useCallback(
    (contextLabel) => {
      if (!isConference || !room) {
        return;
      }

      if (audioPlaybackCleanupRef.current) {
        return;
      }

      const handler = async () => {
        if (room.canPlaybackAudio) {
          avLog.debug("🔊 [Conference audio] Audio already playable, cleaning up listeners");
          clearAudioPlaybackListeners();
          return;
        }

        if (audioPlaybackAttemptingRef.current) {
          return;
        }

        audioPlaybackAttemptingRef.current = true;
        avLog.debug("🔊 [Conference audio] Attempting startAudio", {
          context: contextLabel,
        });

        try {
          await room.startAudio();
          avLog.debug("🔊 [Conference audio] startAudio succeeded", {
            context: contextLabel,
          });
          clearAudioPlaybackListeners();
        } catch (err) {
          avLog.debug("🔊 [Conference audio] startAudio failed", {
            context: contextLabel,
            error: err,
          });
        } finally {
          audioPlaybackAttemptingRef.current = false;
        }
      };

      const cleanup = () => {
        document.removeEventListener("click", handler);
        document.removeEventListener("touchstart", handler);
        document.removeEventListener("keydown", handler);
      };

      audioPlaybackCleanupRef.current = cleanup;
      document.addEventListener("click", handler);
      document.addEventListener("touchstart", handler, { passive: true });
      document.addEventListener("keydown", handler);

      avLog.debug("🔊 [Conference audio] Armed audio playback recovery", {
        context: contextLabel,
      });

      // Try immediately in case a user gesture already occurred
      handler();
    },
    [clearAudioPlaybackListeners, isConference, room]
  );


  useEffect(() => {
    if (!room || !user?.id || useRealtimeApi) {
      return;
    }

    const handleTrackSubscribed = (track, publication, participant) => {
      const trackName = getPublicationTrackName(publication);

      avLog.debug(
        "[CallView] trackSubscribed fired",
        participant?.identity,
        {
          trackSid: publication?.trackSid,
          kind: publication?.track?.kind,
          source: publication?.source,
          trackName,
        }
      );

      if (isConference) {
        avLog.debug("🔍 [Conference audio debug] trackSubscribed event", {
          callUuid: data?.uuid,
          conferenceId: data?.conference_id,
          identity: participant?.identity,
          trackSid: publication?.trackSid,
          source: publication?.source,
          trackName,
          isSubscribed: publication?.isSubscribed,
        });
      }

      if (isConference && participant && isAudioPublication(publication)) {
        if (publication?.isSubscribed === false) {
          avLog.debug("🔊 [Conference audio] trackSubscribed still unsubscribed", {
            identity: participant?.identity,
            trackSid: publication?.trackSid,
            source: publication?.source,
          });
        }
        forceSubscribeConferenceAudio(participant, "trackSubscribed");
        applyConferenceTtsPublicationVolume(
          participant?.identity,
          publication,
          "trackSubscribed-immediate"
        );
        setTimeout(() => {
          applyConferenceTtsPublicationVolume(
            participant?.identity,
            publication,
            "trackSubscribed-delayed-100"
          );
        }, 100);
        setTimeout(() => {
          applyConferenceTtsPublicationVolume(
            participant?.identity,
            publication,
            "trackSubscribed-delayed-400"
          );
        }, 400);
      }
    };

    room.on("trackSubscribed", handleTrackSubscribed);

    return () => {
      room.off("trackSubscribed", handleTrackSubscribed);
    };
  }, [room, useRealtimeApi]);

  // Diagnostic-only: room-level mute/unmute for ANY participant's track
  useEffect(() => {
    if (!room) {
      return;
    }

    const handleTrackMuteChange = (muted) => (publication, participant) => {
      avLog.debug("[CallView] track mute state changed " + JSON.stringify({
        muted,
        identity: participant?.identity,
        isLocal: participant?.isLocal,
        trackSid: publication?.trackSid,
        source: publication?.source,
        trackName: getPublicationTrackName(publication),
      }));
    };

    const handleTrackMuted = handleTrackMuteChange(true);
    const handleTrackUnmuted = handleTrackMuteChange(false);

    room.on(RoomEvent.TrackMuted, handleTrackMuted);
    room.on(RoomEvent.TrackUnmuted, handleTrackUnmuted);

    return () => {
      room.off(RoomEvent.TrackMuted, handleTrackMuted);
      room.off(RoomEvent.TrackUnmuted, handleTrackUnmuted);
    };
  }, [room]);

  useEffect(() => {
    if (!room || !user?.id || useRealtimeApi) {
      return;
    }

    const handleTrackPublished = (publication, participant) => {
      if (publication?.kind === "audio" && participant) {
        avLog.debug("[CallView] trackPublished fired " + participant?.identity + " " + JSON.stringify({
          trackSid: publication?.trackSid,
          source: publication?.source,
          trackName: getPublicationTrackName(publication),
          isMuted: publication?.isMuted,
        }));
      }

      if (isConference && publication?.kind === "audio" && participant) {
        avLog.debug("🔊 [Conference audio] trackPublished seen", {
          identity: participant?.identity,
          trackSid: publication?.trackSid,
          source: publication?.source,
        });
        forceSubscribeConferenceAudio(participant, "trackPublished");
        applyConferenceTtsPublicationVolume(
          participant?.identity,
          publication,
          "trackPublished-immediate"
        );
        setTimeout(() => {
          applyConferenceTtsPublicationVolume(
            participant?.identity,
            publication,
            "trackPublished-delayed-100"
          );
        }, 100);
        setTimeout(() => {
          applyConferenceTtsPublicationVolume(
            participant?.identity,
            publication,
            "trackPublished-delayed-400"
          );
        }, 400);
      }
    };

    room.on("trackPublished", handleTrackPublished);

    return () => {
      room.off("trackPublished", handleTrackPublished);
    };
  }, [room, useRealtimeApi]);

  // Arm audio playback recovery when room connects (for conferences)
  useEffect(() => {
    if (!isConference || !room) {
      return;
    }

    if (room.state === "connected") {
      armConferenceAudioPlayback("room-connected");
    }
  }, [armConferenceAudioPlayback, isConference, room, room?.state]);

  // Re-arm audio playback recovery if playback fails
  useEffect(() => {
    if (!isConference || !room) {
      return;
    }

    const handleAudioPlaybackFailed = (err) => {
      avLog.debug("🔊 [Conference audio] Audio playback failed", {
        error: err,
      });
      armConferenceAudioPlayback("playback-failed");
    };

    room.on(RoomEvent.AudioPlaybackFailed, handleAudioPlaybackFailed);

    return () => {
      room.off(RoomEvent.AudioPlaybackFailed, handleAudioPlaybackFailed);
    };
  }, [armConferenceAudioPlayback, isConference, room]);

  // Cleanup audio playback listeners on unmount or when leaving conference
  useEffect(() => {
    if (!isConference) {
      clearAudioPlaybackListeners();
    }

    return () => {
      clearAudioPlaybackListeners();
    };
  }, [clearAudioPlaybackListeners, isConference]);

  const remoteNonAgentParticipants = useMemo(() => {
    const filtered = remoteParticipant.filter((participant) => {
      if (!participant?.identity) {
        return false;
      }

      if (participant.identity.includes("stt-agent")) {
        return false;
      }

      const agentUserId = parseAgentUserId(participant.identity);
      if (agentUserId) {
        // Hide all agents from the UI, even if this user owns them.
        return false;
      }

      if (isConferenceAgentIdentity(participant.identity)) {
        return false;
      }

      if (isConference && participant.identity.startsWith("agent-")) {
        return false;
      }

      return true;
    });

    return filtered;
  }, [isConference, remoteParticipant]);

  const refreshConferenceCallDetailsSilently = useCallback(() => {
    if (!data?.conference_id || conferenceDetailsRefreshInFlightRef.current) {
      return;
    }

    conferenceDetailsRefreshInFlightRef.current = true;

    getConferenceCallDetailsApiCall(
      data.conference_id,
      (response) => {
        conferenceDetailsRefreshInFlightRef.current = false;

        const callDetails =
          response?.data?.details ||
          response?.data?.call ||
          response?.data?.conference_call;

        if (!callDetails) {
          return;
        }

        const withConferenceFlag = {
          ...callDetails,
          conference_id: data.conference_id,
        };

        if (activeCallRef.current) {
          activeCallRef.current = {
            ...activeCallRef.current,
            details: withConferenceFlag,
            access: response?.data?.access ?? activeCallRef.current?.access,
          };
        }

        setSelectedCall({ ...withConferenceFlag });
      },
      () => {
        conferenceDetailsRefreshInFlightRef.current = false;
      }
    );
  }, [activeCallRef, data?.conference_id, setSelectedCall]);

  const {
    selectedAudioInput,
    setSelectedAudioInput,
    selectedVideoInput,
    setSelectedVideoInput,
    selectedAudioOutput,
    setSelectedAudioOutput,
    backgroundType,
    setBackgoundType,
    remoteNoiseFilterEnabled,
    setRemoteNoiseFilterEnabled,
    updateMuteState,
  } = useCallsService();

  // Krisp noise filter
  const { isNoiseFilterEnabled, isNoiseFilterPending, setNoiseFilterEnabled } = useKrispNoiseFilter();

  const audioInputDevice = useMediaDeviceSelect({ kind: "audioinput" });
  const videoInputDevice = useMediaDeviceSelect({ kind: "videoinput" });
  const audioOutputDevice = useMediaDeviceSelect({ kind: "audiooutput" });


  const [micEnabled, setMicEnabled] = useState(
    !room.localParticipant.isMicrophoneEnabled
  );
  const micEnabledRef = useRef(!room?.localParticipant?.isMicrophoneEnabled);
  const videoEnabledRef = useRef(data.video);
  const videoTogglingRef = useRef(false);
  const canStreamTranscription = useMemo(() => {
    return Boolean(
      isCurrentSpeaker &&
        microphoneStatus.track &&
        !microphoneStatus.isMuted &&
        microphoneStatus.isEnabled
    );
  }, [isCurrentSpeaker, micEnabled, microphoneStatus]);

  const initialConferenceRole = useMemo(() => {
    if (!isConference) {
      return null;
    }
    const prefs = LocalStorage.getConferenceMediaPermissions();
    if (prefs && prefs.callUuid === data?.uuid) {
      return prefs.role;
    }
    return data?.conference_role || null;
  }, [data?.conference_role, data?.uuid, isConference]);

  const [videoEnabled, setVideoEnabled] = useState(data.video);
  const [micAllowed, setMicAllowed] = useState(true);
  const [cameraAllowed, setCameraAllowed] = useState(true);
  const [isConferenceAttendee, setIsConferenceAttendee] = useState(
    initialConferenceRole === CONFERENCE_ROLE_ATTENDEE
  );
  const shouldBypassMicPermissions = isConference && !micAllowed;

  const [grid, setGrid] = useState(false);
  const [callType, setCallType] = useState("");
  const [isParticipantsOpen, setIsParticipantsOpen] = useState(false);
  const [callParticipants, setCallParticipants] = useState([]);
  const [previousSpeakStatus, setPreviousSpeakStatus] = useState(null);
  const [speakStatus, setSpeakStatus] = useState(ENDED);
  const [screenSharing, setScreenSharing] = useState(false);
  const appAudioCaptureRef = useRef(null);

  useEffect(() => {
    const participant = localParticipant.localParticipant;
    if (!participant) return;

    const handleUnpublished = (publication) => {
      if (publication?.source !== Track.Source.ScreenShareAudio) return;
      appAudioCaptureRef.current?.stop();
      appAudioCaptureRef.current = null;
    };

    participant.on(ParticipantEvent.LocalTrackUnpublished, handleUnpublished);
    return () => {
      participant.off(ParticipantEvent.LocalTrackUnpublished, handleUnpublished);
    };
  }, [localParticipant.localParticipant]);

  useEffect(() => {
    return () => {
      appAudioCaptureRef.current?.stop();
      appAudioCaptureRef.current = null;
    };
  }, []);
  const [isPickingShareScreen, setIsPickingShareScreen] = useState(false);
  const [isOptionModalOpen, setIsOptionModalOpen] = useState(false);
  const [videoTrack, setVideoTrack] = useState(null);
  const { notifySuccess, notifyError, notifyInfo, notifyCallChatMessage, notifyCallPoll } = useNotify();
  const canEndConference = Boolean(data?.conference_id) && ["organizer", "presenter"].includes(data?.conference_role);
  const canManageChatToggle = Boolean(data?.conference_id) && ["organizer", "presenter"].includes(data?.conference_role) && !isMobile;
  const isSoloConference = useMemo(() => {
    if (!isConference) return false;
    const isHost = [CONFERENCE_ROLE_ORGANIZER, CONFERENCE_ROLE_PRESENTER].includes(
      data?.conference_role
    );
    return isHost && (data?.other_participants?.length ?? 0) === 0;
  }, [isConference, data?.conference_role, data?.other_participants]);
  const processedMessages = useRef(new Set());
  const micPublishAttemptedRef = useRef(false);

  useEffect(() => {
    if (!isConference || isConferenceAttendee || !micAllowed) {
      return;
    }

    if (microphoneStatus.track) {
      micPublishAttemptedRef.current = false;
      return;
    }

    if (microphoneStatus.isEnabled || micPublishAttemptedRef.current) {
      return;
    }

    const participant = localParticipant.localParticipant;
    if (!participant) {
      // not guaranteed to be ready at this point! 
      // retry on a later render without poisoning the guard.
      return;
    }

    micPublishAttemptedRef.current = true;
    participant
      .setMicrophoneEnabled(true)
      .then((publication) => {
        avLog.debug("🔊 [Conference audio] Safety-net microphone publish succeeded");
        if (micEnabledRef.current) {
          (publication?.track ?? participant.getTrackPublication(Track.Source.Microphone)?.track)?.mute();
        }
      })
      .catch((err) => {
        avLog.debug("🔊 [Conference audio] Safety-net microphone publish failed", err);
        micPublishAttemptedRef.current = false;
      });
  }, [
    isConference,
    isConferenceAttendee,
    micAllowed,
    microphoneStatus,
    localParticipant.localParticipant,
  ]);

  const publishedTracks = useTracks();

  const [participantIsSharingScreen, setParticipantIsSharingScreen] =
    useState(null);
  const [screenSharingParticipantsList, setScreenSharingParticipantsList] =
    useState(null);
  const [manuallySelectedScreenShare, setManuallySelectedScreenShare] = useState(false);
  const [previousScreenSharers, setPreviousScreenSharers] = useState([]);
  const [defaultScreenSharer, setDefaultScreenSharer] = useState(null);

  const [isSpeakingWhileMuted, setIsSpeakingWhileMuted] = useState(false);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const micStreamRef = useRef(null);
  const speakingWhileMutedFrameCount = useRef(0);
  const lastNotifiedAt = useRef(0); // timestamp in ms

  const [leftPanelView, setLeftPanelView] = useState(null);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const [rightPanelAsOverlay, setRightPanelAsOverlay] = useState(false);
  const [cameraStripCollapsed, setCameraStripCollapsed] = useState(false);
  const [controlBarVisible, setControlBarVisible] = useState(true);
  const controlBarHideTimerRef = useRef(null);
  const translationTypeRef = useRef(translationType);
  const [callChatConversation, setCallChatConversation] = useState(null);
  const [unreadCallChatCount, setUnreadCallChatCount] = useState(0);
  const [isCheckingConversation, setIsCheckingConversation] = useState(false);
  const seenMessageIds = useRef(new Set());
  const leftPanelViewRef = useRef(leftPanelView);
  const notifyCallChatMessageRef = useRef(notifyCallChatMessage);
  const tRef = useRef(t);
  const callChatConversationIdRef = useRef(null);
  const callJoinedTimestampRef = useRef(Date.now());
  const [pollsAllowed, setPollsAllowed] = useState(false);
  const [activePollBadge, setActivePollBadge] = useState(false);
  const pendingMessagesRef = useRef([]);
  const processMessageRef = useRef(null);

  const isLocalSpeaking = useMemo(() => {
    return activeSpeakers.some((participant) => participant?.isLocal === true);
  }, [activeSpeakers]);

  const lastHumanSpeakerIdentityRef = useRef(null);
  const interpretedIdentityRef = useRef(null);

  useEffect(() => {
    const humanSpeaker = activeSpeakers.find((participant) => {
      if (!participant || participant.isLocal) return false;
      const identity = participant.identity;
      if (!identity || typeof identity !== "string") return false;
      const parts = identity.split("|");
      if (parts.length >= 3 && parts[0] === AGENT_IDENTITY_PREFIX) return false;
      return true;
    });

    if (humanSpeaker?.identity) {
      lastHumanSpeakerIdentityRef.current = humanSpeaker.identity;
    }
  }, [activeSpeakers]);

  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);

  useEffect(() => {
    const agentActive = activeSpeakers.some((participant) => {
      const identity = participant?.identity;
      if (!identity || typeof identity !== "string") return false;
      const parts = identity.split("|");
      if (parts.length < 3 || parts[0] !== AGENT_IDENTITY_PREFIX) return false;
      return String(parts[1]) === String(user?.id);
    });

    if (agentActive) {
      if (agentSpeakingHoldTimerRef.current) {
        clearTimeout(agentSpeakingHoldTimerRef.current);
        agentSpeakingHoldTimerRef.current = null;
      }
      if (!agentSpeakingHeldRef.current) {
        agentSpeakingHeldRef.current = true;
        setIsAgentSpeaking(true);
      }
    } else {
      if (agentSpeakingHeldRef.current && !agentSpeakingHoldTimerRef.current) {
        agentSpeakingHoldTimerRef.current = setTimeout(() => {
          agentSpeakingHeldRef.current = false;
          agentSpeakingHoldTimerRef.current = null;
          setIsAgentSpeaking(false);
        }, 400);
      }
    }
  }, [activeSpeakers, user?.id]);

  useEffect(() => {
    return () => {
      if (agentSpeakingHoldTimerRef.current) {
        clearTimeout(agentSpeakingHoldTimerRef.current);
        agentSpeakingHoldTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isConference) return;

    if (isAgentSpeaking) {
      const speakerIdentity = lastHumanSpeakerIdentityRef.current;
      if (!speakerIdentity) return;
      interpretedIdentityRef.current = speakerIdentity;
      window.dispatchEvent(
        new CustomEvent("emotii-playback-start", {
          detail: { userId: speakerIdentity },
        })
      );
      return;
    }

    const startedIdentity = interpretedIdentityRef.current;
    if (!startedIdentity) return;
    interpretedIdentityRef.current = null;
    window.dispatchEvent(
      new CustomEvent("emotii-playback-end", {
        detail: { userId: startedIdentity },
      })
    );
  }, [isAgentSpeaking, isConference]);

  const finalIncomingVolume = useMemo(() => {
    const base = (incomingVolume / 100) * INCOMING_VOICE_MAX_GAIN;
    const emotiiF = emotiiVolume / 100;
    if (dimEnabled && isAgentSpeaking && emotiiVolume > 0) {
      return Math.min(base, emotiiF * 0.3);
    }
    return base;
  }, [dimEnabled, isAgentSpeaking, incomingVolume, emotiiVolume]);

  useEffect(() => {
    if (!isConference) return;
    avLog.debug("🎤 [Conference audio debug] Active speakers (server-detected audio level)", {
      callUuid: data?.uuid,
      conferenceId: data?.conference_id,
      speakers: activeSpeakers.map((participant) => ({
        identity: participant?.identity,
        isLocal: participant?.isLocal,
        audioLevel: participant?.audioLevel,
      })),
    });
  }, [isConference, activeSpeakers, data?.uuid, data?.conference_id]);

  const shouldSuppressConferenceTtsWhileLocalSpeaking = useMemo(() => {
    return Boolean(isConference && isLocalSpeaking);
  }, [isConference, isLocalSpeaking]);

  const handleIncomingVolumeChange = useCallback((volume, isDimEnabled) => {
    setIncomingVolume(volume);
    setDimEnabled(isDimEnabled);
    LocalStorage.setIncomingVoiceVolume(data?.uuid, volume);
    LocalStorage.setDimVoiceEnabled(isDimEnabled);
  }, [data?.uuid]);

  const handleEmotiiVolumeChange = useCallback((volume) => {
    const nextIsEmotiiAudioEnabled = isConference
      ? volume > 0
      : Boolean(emotiiVoice && volume > 0);

    if (isConference) {
      avLog.debug("🔍 [Conference audio debug] Emotii volume slider changed", {
        callUuid: data?.uuid,
        conferenceId: data?.conference_id,
        isEmotiiAudioEnabled,
        nextIsEmotiiAudioEnabled,
        emotiiVoice,
        previousEmotiiVolume: emotiiVolume,
        nextEmotiiVolume: volume,
      });
    }

    if (!isConference && volume === 0) {
      (remoteParticipantsRef.current || []).forEach((participant) => {
        const agentUserId = parseAgentUserId(participant?.identity);
        if (!agentUserId || String(agentUserId) !== String(user?.id)) return;
        const pubs = participant.trackPublications instanceof Map
          ? Array.from(participant.trackPublications.values())
          : [];
        pubs.forEach((pub) => {
          if (isAudioPublication(pub)) {
            setPublicationTrackVolume(pub, 0);
          }
        });
      });
    }

    setEmotiiVolume(volume);
    LocalStorage.setEmotiiVoiceVolume(data?.uuid, volume);
    if (onEmotiiVolumeChange) {
      onEmotiiVolumeChange(volume);
    }
  }, [onEmotiiVolumeChange, data?.uuid]);

  const applyVolumesToParticipants = useCallback(() => {
    if (!remoteParticipant || remoteParticipant.length === 0) {
      return;
    }

    const isTranslationDisabled = translationType === DISABLED_TRANSLATION;

    const baseIncomingVolume = (incomingVolume / 100) * INCOMING_VOICE_MAX_GAIN;
    const finalEmotiiVolume = emotiiVolume / 100;
    const finalIncomingVolume = (dimEnabled && isAgentSpeaking && emotiiVolume > 0)
      ? Math.min(baseIncomingVolume, finalEmotiiVolume * 0.3)
      : baseIncomingVolume;

    if (isConference) {
      avLog.debug("🔍 [Conference audio debug] Applying volume sweep", {
        callUuid: data?.uuid,
        conferenceId: data?.conference_id,
        participantCount: remoteParticipant.length,
        isEmotiiAudioEnabled,
        emotiiVoice,
        incomingVolume,
        emotiiVolume,
        translationType,
      });
    }

    remoteParticipant.forEach((participant) => {
      if (participant.isLocal) return;

      const identity = participant?.identity;
      const agentUserId = parseAgentUserId(identity);
      const isAgent =
        Boolean(agentUserId) ||
        (isConference &&
          typeof identity === "string" &&
          (identity.startsWith("agent-") ||
            identity.includes("stt-agent") ||
            isConferenceAgentIdentity(identity)));
      const isOwnAgent =
        Boolean(agentUserId) && String(agentUserId) === String(user?.id);

      const audioPublications = participant.trackPublications
        ? Array.from(participant.trackPublications.values()).filter((pub) =>
            isAudioPublication(pub)
          )
        : [];

      audioPublications.forEach((publication) => {
        const track = publication.track;
        if (!track) return;

        const trackName = getPublicationTrackName(publication);
        const isConferenceLanguageTrack = isConferenceLanguageAgentTrack(
          identity,
          trackName
        );
        const isSelectedConferenceLanguageTrack =
          isConferenceLanguageTrack &&
          conferenceTrackMatchesSelectedLanguage(identity, trackName);

        let targetVolume;

        if (isTranslationDisabled) {
          if (isAgent || isConferenceLanguageTrack) {
            targetVolume = 0;
          } else {
            targetVolume = 1.0;
          }
        } else {
          if (isOwnAgent || isConferenceLanguageTrack) {
            if (!isEmotiiAudioEnabled) {
              targetVolume = 0;
            } else if (isConferenceLanguageTrack && !isSelectedConferenceLanguageTrack) {
              targetVolume = 0;
            } else if (
              isConferenceLanguageTrack &&
              isSelectedConferenceLanguageTrack &&
              shouldSuppressConferenceTtsWhileLocalSpeaking
            ) {
              targetVolume = 0;
            } else {
              targetVolume = finalEmotiiVolume;
            }
          } else if (isAgent) {
            targetVolume = 0;
          } else {
            targetVolume = finalIncomingVolume;
          }
        }

        avLog.debug("[CallView] Computed track volume " + JSON.stringify({
          isConference,
          callUuid: data?.uuid,
          conferenceId: data?.conference_id,
          identity,
          trackSid: publication?.trackSid,
          source: publication?.source,
          trackName,
          isSubscribed: publication?.isSubscribed,
          isMuted: publication?.isMuted ?? track?.isMuted,
          isAgent,
          isOwnAgent,
          isConferenceLanguageTrack,
          isSelectedConferenceLanguageTrack,
          isTranslationDisabled,
          isEmotiiAudioEnabled,
          shouldSuppressConferenceTtsWhileLocalSpeaking,
          emotiiVoice,
          emotiiVolume,
          finalIncomingVolume,
          finalEmotiiVolume,
          targetVolume,
        }));

        setPublicationTrackVolume(
          { ...publication, participantIdentity: identity },
          targetVolume,
          "applyVolumesToParticipants"
        );

        if (isConferenceLanguageTrack) {
          setPublicationTrackPlaybackRate(
            { ...publication, participantIdentity: identity },
            isSelectedConferenceLanguageTrack ? conferenceTtsPlaybackRate : 1.0,
            "applyVolumesToParticipants"
          );
        }
      });
    });
  }, [
    remoteParticipant,
    incomingVolume,
    dimEnabled,
    isAgentSpeaking,
    emotiiVolume,
    emotiiVoice,
    isEmotiiAudioEnabled,
    user?.id,
    translationType,
    conferenceLanguageKey,
    conferenceTtsTrackName,
    conferenceTtsPlaybackRate,
    shouldSuppressConferenceTtsWhileLocalSpeaking,
    isConference,
    data?.conference_id,
    data?.uuid,
  ]);

  useEffect(() => {
    applyVolumesToParticipants();
    const id = setTimeout(applyVolumesToParticipants, 250);
    return () => clearTimeout(id);
  }, [applyVolumesToParticipants]);

  // Sync UI state when admin mutes/unmutes via server
  useEffect(() => {
    let pollInterval = null;
    let waitForTrack = null;

    const teardownPolling = () => {
      if (waitForTrack) {
        clearInterval(waitForTrack);
        waitForTrack = null;
      }
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const setupPolling = (track) => {
      teardownPolling();

      // Set initial state based on current track state
      const initialMuted = track.isMuted;
      micEnabledRef.current = initialMuted;
      setMicEnabled(initialMuted);

      // Poll track mute state to detect server-side mute changes
      pollInterval = setInterval(() => {
        const isMuted = track.isMuted;
        const expectedMicEnabled = isMuted;

        if (micEnabledRef.current !== expectedMicEnabled) {
          micEnabledRef.current = expectedMicEnabled;
          setMicEnabled(expectedMicEnabled);
        }
      }, 500);
    };

    const handleLocalTrackPublished = (publication, participant) => {
      if (participant.isLocal && publication.source === Track.Source.Microphone && publication.track) {
        setupPolling(publication.track);
      }
    };

    const micTrackPub = localParticipant.localParticipant?.getTrackPublication(Track.Source.Microphone);

    if (!micTrackPub?.track) {
      // Track not available yet, poll until it is
      waitForTrack = setInterval(() => {
        const pub = localParticipant.localParticipant?.getTrackPublication(Track.Source.Microphone);
        if (pub?.track) {
          clearInterval(waitForTrack);
          waitForTrack = null;
          setupPolling(pub.track);
        }
      }, 100);
    } else {
      setupPolling(micTrackPub.track);
    }

    room?.on(RoomEvent.LocalTrackPublished, handleLocalTrackPublished);

    return () => {
      teardownPolling();
      room?.off(RoomEvent.LocalTrackPublished, handleLocalTrackPublished);
    };
  }, [localParticipant.localParticipant, room]);

  useEffect(() => {
    const syncInterval = setInterval(() => {
      if (videoTogglingRef.current) return;
      const actual = localParticipant.localParticipant?.isCameraEnabled ?? false;
      if (videoEnabledRef.current !== actual) {
        videoEnabledRef.current = actual;
        setVideoEnabled(actual);
      }
    }, 500);
    return () => clearInterval(syncInterval);
  }, [localParticipant.localParticipant]);

  const [shareScreenAllowed, setShareScreenAllowed] = useState(true);
  const [chatAllowed, setChatAllowed] = useState(true);

  const handleMuteBtn = async () => {
    if (!micAllowed) {
      return;
    }
    let microphone = localParticipant.localParticipant.getTrackPublication(
      Track.Source.Microphone
    );
    if (microphone && !micEnabled) {
      muteMicrophone();
    } else {
      unmuteMicrophone();
    }
  };

  const waitForMicrophoneTrack = (timeoutMs = 5000) =>
    new Promise((resolve) => {
      const participant = localParticipant?.localParticipant;
      if (!participant) {
        resolve(null);
        return;
      }
      const existing = participant.getTrackPublication(
        Track.Source.Microphone
      )?.track;
      if (existing) {
        resolve(existing);
        return;
      }
      const cleanup = () => {
        clearTimeout(timer);
        participant.off(ParticipantEvent.LocalTrackPublished, onPublished);
      };
      const onPublished = (publication) => {
        if (
          publication?.source === Track.Source.Microphone &&
          publication.track
        ) {
          cleanup();
          resolve(publication.track);
        }
      };
      const timer = setTimeout(() => {
        cleanup();
        resolve(null);
      }, timeoutMs);
      participant.on(ParticipantEvent.LocalTrackPublished, onPublished);
    });

  // last intent wins: stale calls parked in waitForMicrophoneTrack must not clobber newer ones
  const micOpSeqRef = useRef(0);

  const setMicrophoneMuted = async (muted) => {
    const opId = ++micOpSeqRef.current;

    let track = localParticipant.localParticipant.getTrackPublication(
      Track.Source.Microphone
    )?.track;

    if (!track) {
      track = await waitForMicrophoneTrack();
      if (opId !== micOpSeqRef.current) {
        return;
      }
      if (!track) {
        // timed out — sync UI with actual mic state
        const publication =
          localParticipant.localParticipant.getTrackPublication(
            Track.Source.Microphone
          );
        const actuallyMuted = publication?.track?.isMuted ?? true;
        micEnabledRef.current = actuallyMuted;
        setMicEnabled(actuallyMuted);
        return;
      }
    }

    if (muted) {
      await track.mute();
    } else {
      await track.unmute();
    }

    if (opId !== micOpSeqRef.current) {
      return;
    }
    micEnabledRef.current = muted;
    setMicEnabled(muted);

    if (!isConference && data?.id) {
      updateMuteState(
        data.id,
        muted,
        () => {},
        (error) => {
          avLog.debug("Failed to report own mute state", error);
        }
      );
    }
  };

  const muteMicrophone = () => setMicrophoneMuted(true);
  const unmuteMicrophone = () => setMicrophoneMuted(false);


  const disableMicrophone = async () => {
    try {
      // Get the user's microphone
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Stop all audio tracks
      stream.getAudioTracks().forEach((track) => {
        track.stop(); // This stops the microphone completely
      });
    } catch (err) {
      console.error("Error disabling microphone:", err);
    }
  };

  const handleDisconect = async (e) => {

    isLeavingCallRef.current = true;

    if (room?.state === "connecting") {
      return;
    }
    await disableMicrophone();
    LocalStorage.removeConferenceMediaPermissions();
    e?.preventDefault();
    room.disconnect();
    handleSendCancelSpeakMessage();
    callingSoundHowl.stop();
    micEnabledRef.current = true;
    translationEnabledRef.current = false;
    resetCall();
  };

  const handleEndConference = () => {
    if (!data?.conference_id) {
      return;
    }
    setLoading(true);
    endConferenceApiCall(
      data.conference_id,
      () => {
        setLoading(false);
        handleDisconect();
      },
      () => {
        setLoading(false);
        notifyError(t("general.something_wrong"));
      }
    );
  };

  const handleToggleChatSetting = (nextAllowChat) => {
    if (!data?.conference_id) {
      return;
    }
    toggleConferenceChatApiCall(
      data.conference_id,
      nextAllowChat,
      () => {},
      () => {
        notifyError(t("general.something_wrong"));
      }
    );
  };

  const publishVideoTrack = async () => {
    if (videoTogglingRef.current) return;
    videoTogglingRef.current = true;
    if (!cameraAllowed) {
      videoEnabledRef.current = false;
      setVideoEnabled(false);
      videoTogglingRef.current = false;
      return;
    }
    try {
      const permission = await checkCameraPermission();

      if (permission === "granted") {
        let videoTrackPublication =
          localParticipant.localParticipant.getTrackPublication(
            Track.Source.Camera
          );

        if (!videoTrackPublication) {
          const track = await createLocalVideoTrack(
            isConference ? { resolution: VideoPresets.h1080.resolution } : undefined
          );
          await applyVirtualBackground(track);
          await localParticipant.localParticipant.publishTrack(
            track,
            isConference
              ? { simulcast: true, videoEncoding: VideoPresets.h1080.encoding }
              : undefined
          );
          setVideoTrack(track);
          videoEnabledRef.current = true;
          setVideoEnabled(true);
        } else {
          await applyVirtualBackground(videoTrackPublication.track);
          setVideoTrack(videoTrackPublication.track);
          videoEnabledRef.current = true;
          setVideoEnabled(true);
        }
      } else if (permission === "denied") {
        notifyError(t('misc.video_access'));
      }
    } catch (error) {
      notifyError(t('misc.video_access'));
    } finally {
      videoTogglingRef.current = false;
    }
  };

  const applyVirtualBackground = async (videoTrack) => {
    if (!videoTrack) return;

    let localStoredVirtualBgValue = LocalStorage.getCallVirtualBg();

    if (localStoredVirtualBgValue) {
      if (localStoredVirtualBgValue === BLUR_BG) {
        await setVirtualBackground(videoTrack, BLUR_BG, null);
      } else if (localStoredVirtualBgValue === MORE_BLUR_BG) {
        await setVirtualBackground(videoTrack, MORE_BLUR_BG, null);
      } else if (localStoredVirtualBgValue === NORMAL_BG) {
        await setVirtualBackground(videoTrack, NORMAL_BG, null);
      } else {
        await setVirtualBackground(
          videoTrack,
          IMAGE_BG,
          localStoredVirtualBgValue
        );
      }
    } else {
      await setVirtualBackground(videoTrack, NORMAL_BG, null);
    }
  };

  const handleVirtualBackground = async (bgType, imageLink) => {
    const videoTrackPublication =
      localParticipant.localParticipant.getTrackPublication(
        Track.Source.Camera
      );
    if (videoTrackPublication && videoTrackPublication.track) {
      const videoTrack = videoTrackPublication.track;
      setLoading(true);

      if (videoTrack.processor) {
        avLog.debug("Processor already exists:", videoTrack.processor);
        await videoTrack.stopProcessor();
      }

      try {
        await setVirtualBackground(videoTrack, bgType, imageLink);
        saveBackgroundLocal(bgType, imageLink);
        setLoading(false);
      } catch (error) {
        console.error("Error applying blur:", error);
        setLoading(false);
      }
    } else {
      saveBackgroundLocal(bgType, imageLink);
      console.error("No video track found.");
      setLoading(false);
    }
  };

  const saveBackgroundLocal = (bgType, imageLink) => {
    if (imageLink) {
      setBackgoundType(imageLink);
      LocalStorage.setCallVirtualBg(imageLink);
    } else {
      setBackgoundType(bgType);
      LocalStorage.setCallVirtualBg(bgType);
    }
  };

  const setVirtualBackground = async (videoTrack, bgType, imageLink) => {
    if (!videoTrack) {
      return;
    }

    if (bgType === NORMAL_BG) {
      await videoTrack.stopProcessor();
    }
    if (bgType === BLUR_BG || bgType === MORE_BLUR_BG) {
      const blur = BackgroundBlur(bgType === MORE_BLUR_BG ? 16 : 10);
      await videoTrack.setProcessor(blur); // Apply blur
    }
    if (bgType === IMAGE_BG) {
      const imageUrl = imageLink; // Provide the correct path
      const imageProcessor = await VirtualBackground(imageUrl);
      await videoTrack.setProcessor(imageProcessor); // Apply image background
    }
  };

  const checkCameraPermission = async () => {
    try {
      if (!navigator.permissions) {
        throw new Error("Permissions API not supported");
      }
      const result = await navigator.permissions.query({ name: "camera" });

      if (result.state === "prompt") {
        return requestCameraPermission();
      } else {
        return result.state;
      }
    } catch (err) {
      console.error("Error checking camera permissions:", err);
      return;
    }
  };

  const requestCameraPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((track) => track.stop()); // Release the camera
      return "granted";
    } catch (err) {
      console.error("Error requesting camera permissions:", err.message);
      return "denied";
    }
  };

  const handleUnpublishVideo = async () => {
    if (videoTogglingRef.current) return;
    videoTogglingRef.current = true;
    try {
      let videoTrackPublication =
        localParticipant.localParticipant.getTrackPublication(
          Track.Source.Camera
        );
      if (localParticipant && videoTrackPublication) {
        await localParticipant.localParticipant.unpublishTrack(
          videoTrackPublication.track
        );
        videoEnabledRef.current = false;
        setVideoEnabled(false);
        setVideoTrack(null);
      }
    } catch (error) {
      // leave state as-is; the sync poll will reconcile
    } finally {
      videoTogglingRef.current = false;
    }
  };

  useEffect(() => {
    const prefs = LocalStorage.getConferenceMediaPermissions();
    const hasPrefs = prefs && prefs.callUuid === data?.uuid;
    const role = hasPrefs ? prefs.role : data?.conference_role;
    const isAttendeeRole = role === CONFERENCE_ROLE_ATTENDEE;

    if (!hasPrefs) {
      const attendeeInConference = isConference && isAttendeeRole;

      setMicAllowed(!attendeeInConference);
      setCameraAllowed(true);
      setShareScreenAllowed(!attendeeInConference);
      setPollsAllowed(isConference);
      setIsConferenceAttendee(attendeeInConference);

      if (attendeeInConference) {
        micEnabledRef.current = false;
        const microphone =
          localParticipant?.localParticipant?.getTrackPublication(
            Track.Source.Microphone
          );
        if (microphone?.track?.mute) {
          microphone.track.mute();
        }
      }
      return;
    }

    setMicAllowed(!!prefs.micAllowed);
    setCameraAllowed(!!prefs.cameraAllowed);
    setShareScreenAllowed(prefs.role !== CONFERENCE_ROLE_ATTENDEE);
    setPollsAllowed(isConference && !!prefs.pollsAllowed);
    setIsConferenceAttendee(prefs.role === CONFERENCE_ROLE_ATTENDEE);

    if (!prefs.micAllowed) {
      micEnabledRef.current = false;
      // setMicEnabled(false);
      const microphone =
        localParticipant?.localParticipant?.getTrackPublication(
          Track.Source.Microphone
        );
      if (microphone?.track?.mute) {
        microphone.track.mute();
      }
    }

    if (!prefs.cameraAllowed) {
      handleUnpublishVideo();
    }
  }, [data?.conference_role, data?.uuid, isConference, localParticipant]);

  useEffect(() => {
    const prefs = LocalStorage.getConferenceMediaPermissions();
    const hasPrefs = prefs && prefs.callUuid === data?.uuid;
    setChatAllowed(hasPrefs ? !!prefs.chatAllowed : true);
  }, [data?.conference_role, data?.uuid, isConference]);

  const mapCallParticipants = (call) => {
    if (!userRef.current) {
      return;
    }

    const uniqueIds = new Set();
    uniqueIds.add(call.created_by.id);
    call.other_participants.forEach((item) => uniqueIds.add(item.id));
    uniqueIds.add(userRef.current.id);
    const combined = [
      userRef.current,
      call.created_by,
      call.other_participants,
    ].flat(); // Flatten in case other_participants is array
    const seenIds = new Set();
    const filteredObjects = combined.filter((obj) => {
      if (uniqueIds.has(obj.id) && !seenIds.has(obj.id)) {
        seenIds.add(obj.id); // Track seen IDs
        return true; // Keep the object
      }
      return false; // Exclude duplicates
    });
    setCallParticipants(filteredObjects);
  };

  const handleToggleChat = () => {
    setLeftPanelView((prev) => {
      const newView = prev === "chat" ? null : "chat";
      if (newView === "chat") {
        setUnreadCallChatCount(0);
        if (!callChatConversation && data?.id) {
          setIsCheckingConversation(true);
          const isConferenceCall = !!data.conference_id;
          checkCallChatConversation(
            data.id,
            (response) => {
              const conversation = response.data.conversation;
              if (conversation?.id) {
                setCallChatConversation(conversation);
                callChatConversationIdRef.current = conversation.id;
              }
              setIsCheckingConversation(false);
            },
            (error) => {
              setIsCheckingConversation(false);
            },
            isConferenceCall
          );
        }
      }
      return newView;
    });
  };

  const handleTogglePolls = () => {
    setLeftPanelView((prev) => {
      const newView = prev === "polls" ? null : "polls";
      
      if (newView === "polls") {
        setActivePollBadge(false);
      }
      
      return newView;
    });
  };

  const handleToggleParticipants = () => {
    setLeftPanelView((prev) => (prev === "participants" ? null : "participants"));
  };

  const handleCloseLeftPanel = () => {
    setLeftPanelView(null);
  };

  const handleVideoBtn = (e) => {
    if (!cameraAllowed || videoTogglingRef.current) return;
    if (localParticipant) {
      if (!videoEnabledRef.current) {
        publishVideoTrack();
      } else {
        handleUnpublishVideo();
      }
    }
  };

  const hansleParticipantsBtn = () => {
    setIsParticipantsOpen(!isParticipantsOpen);
  };

  const verifyCallType = (call) => {
    if (call.other_participants.length > 1) {
      setCallType(GROUPCALL);
    } else {
      setCallType(ONECALL);
    }
  };

  const displaySingleCall = () => {
    if (!grid) {
      return (
        <div
          style={{
            display: "flex",
            width: "100%",
            height: "calc(100% - 80px)",
            position: "relative",
          }}
          ref={containerRef}
        >
          <ParticipantComponent
            participant={remoteNonAgentParticipants[0]}
            callInfo={data}
            grid={false}
          />
          {localParticipant.isCameraEnabled && (
            <SmallOwnCameraComponent
              participant={localParticipant.localParticipant}
              containerRef={containerRef}
            />
          )}
        </div>
      );
    } else {
      return (
        <div
          style={{
            display: "flex",
            gap: "20px",
            width: "100%",
            height: "100%",
          }}
        >
          <ParticipantComponent
            key={"local"}
            localParticipant={true}
            participant={localParticipant.localParticipant}
            callInfo={data}
            grid={false}
          />
          <ParticipantComponent
            key={"remote"}
            participant={remoteNonAgentParticipants[0]}
            callInfo={data}
            grid={false}
          />
        </div>
      );
    }
  };

  const handleSwitchGrid = () => {
    setGrid(!grid);
  };

  const handleShareScreenBtn = () => {
    if (!shareScreenAllowed) {
      return;
    }
    if (screenSharing) {
      stopScreenShare();
    } else {
      startScreenShare();
    }
  };

  const startScreenShare = async () => {
    const isElectron = !!window.electron;
    if (isElectron) {
      setIsPickingShareScreen(true);
    } else {
      startScreenShareWeb();
    }
  };

 
  const handleShareDesktopWindowSelected = async (sourceId) => {
    setIsPickingShareScreen(false);

    if (!sourceId) {
      console.error("CallView: No screen source selected.");
      return;
    }

    let appAudio = null;

    try {
      await window.electron.setScreenShareSource(sourceId);

      appAudio = await startAppAudioTrack(sourceId);

      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: !appAudio,
      });

      const videoMediaTrack = stream.getVideoTracks()[0];
      const audioMediaTrack =
        appAudio?.track || stream.getAudioTracks()[0] || null;

      if (!videoMediaTrack) {
        console.error("CallView: getDisplayMedia returned no video track.");
        appAudio?.stop();
        return;
      }

      const screenVideoTrack = new LocalVideoTrack(videoMediaTrack, {
        kind: Track.Source.ScreenShare,
        source: Track.Source.ScreenShare,
      });

      await localParticipant.localParticipant.publishTrack(screenVideoTrack, {
        source: Track.Source.ScreenShare,
      });

      if (audioMediaTrack) {
        const screenAudioTrack = new LocalAudioTrack(audioMediaTrack, {
          kind: Track.Source.ScreenShareAudio,
          source: Track.Source.ScreenShareAudio,
        });

        await localParticipant.localParticipant.publishTrack(screenAudioTrack, {
          source: Track.Source.ScreenShareAudio,
        });

        if (appAudio) {
          appAudioCaptureRef.current?.stop();
          appAudioCaptureRef.current = appAudio;
        }
      }

      setScreenSharing(true);
      setManuallySelectedScreenShare(false);
    } catch (error) {
      appAudio?.stop();
      console.error("Failed to share screen:", error);
    }
  };

  const startScreenShareWeb = () => {
    try {
      localParticipant.localParticipant
        .setScreenShareEnabled(true,
          {
            audio: true
          }
        )
        .then(() => {
          setScreenSharing(true);
          setManuallySelectedScreenShare(false);
        })
        .catch(() => {});
    } catch (error) {
      console.error("Failed to share screen:", error);
    }
  };

  const stopScreenShare = () => {
    localParticipant.localParticipant
      .setScreenShareEnabled(false)
      .then(() => {
        setScreenSharing(false);
      })
      .catch(() => {});
  };

  const publishedTracksObserver = () => {
    if (!publishedTracks) {
      return;
    }
    handlePublishedScreenSharingTracks();
    handlePublishedVideoTracks();
  };

  const handlePublishedScreenSharingTracks = () => {
    let foundScreenSharingTracks = publishedTracks.filter(
      (track) => track.source === "screen_share"
    );

    if (foundScreenSharingTracks.length > 0) {
      setScreenSharingParticipantsList(foundScreenSharingTracks);

      const currentSharerIds = foundScreenSharingTracks.map(
        track => track.participant.identity
      );

      const newSharer = foundScreenSharingTracks.find(
        track => !previousScreenSharers.includes(track.participant.identity)
      );

      const someoneStopped = previousScreenSharers.some(
        identity => !currentSharerIds.includes(identity)
      );

      setPreviousScreenSharers(currentSharerIds);

      if (manuallySelectedScreenShare) {
        const stillSharing = foundScreenSharingTracks.find(
          track => track.participant.identity === participantIsSharingScreen?.participant.identity
        );

        if (!stillSharing) {
          const defaultSharer = newSharer || foundScreenSharingTracks[0];
          setParticipantIsSharingScreen(defaultSharer);
          setDefaultScreenSharer(defaultSharer);
          setManuallySelectedScreenShare(false);
        } else {
          if (newSharer) {
            setDefaultScreenSharer(newSharer);
          } else if (someoneStopped) {
            setDefaultScreenSharer(foundScreenSharingTracks[0]);
          }
        }
      }
      else {
        let defaultSharer = null;        

        if (newSharer) {
          defaultSharer = newSharer;
          setParticipantIsSharingScreen(newSharer);
        }
        else if (someoneStopped) {
          defaultSharer = foundScreenSharingTracks[0];
          setParticipantIsSharingScreen(foundScreenSharingTracks[0]);
        }
        else if (!participantIsSharingScreen) {
          defaultSharer = foundScreenSharingTracks[0];
          setParticipantIsSharingScreen(foundScreenSharingTracks[0]);
        }
        
        if (defaultSharer) {
          setDefaultScreenSharer(defaultSharer);
        }
      }
      
      setGrid(false);
    } else {
      setParticipantIsSharingScreen(null);
      setScreenSharingParticipantsList([]);
      setScreenSharing(false);
      setManuallySelectedScreenShare(false);
      setPreviousScreenSharers([]);
      setDefaultScreenSharer(null);
      setCameraStripCollapsed(false);
    }
  };

  const handlePublishedVideoTracks = () => {
    const foundLocalVideoTrack = publishedTracks.find(
      (track) =>
        track.source === Track.Source.Camera && track.participant.isLocal
    )?.publication?.track;

    if (foundLocalVideoTrack && foundLocalVideoTrack !== videoTrack) {
      setVideoTrack(foundLocalVideoTrack);
      applyVirtualBackground(foundLocalVideoTrack);
    }
  };

  const handleSelectScreenShareParticipant = (participant) => {
    const isCurrentlySelected = 
      participantIsSharingScreen?.participant.identity === participant.participant.identity && 
      manuallySelectedScreenShare;

    if (isCurrentlySelected) {
      setManuallySelectedScreenShare(false);
      
      if (defaultScreenSharer) {
        setParticipantIsSharingScreen(defaultScreenSharer);
      } else if (screenSharingParticipantsList && screenSharingParticipantsList.length > 0) {
        setParticipantIsSharingScreen(screenSharingParticipantsList[0]);
      }
    } else {
      setParticipantIsSharingScreen(participant);
      setManuallySelectedScreenShare(true);
    }
  };

  const handleCloseOptionModal = () => {
    setIsOptionModalOpen(false);
  };
  const handleOpenOptionModal = () => {
    setIsOptionModalOpen(true);
  };

  const handleSelectTranslationType = (selectedTranslationType) => {
    const canSwitchConferenceTranslationType =
      !data?.conference_id ||
      [CONFERENCE_ROLE_ORGANIZER, CONFERENCE_ROLE_PRESENTER].includes(
        data?.conference_role
      );

    if (!canSwitchConferenceTranslationType) {
      return;
    }
    handleSendTranslationEnabledMessage(selectedTranslationType, {
      userInitiated: true,
    });
  };

  const handleLanguageChange = (newLanguage) => {
    if (!isConference) return;
    handleSendTranslationEnabledMessage(translationType);
  };

  const [isRecordingLoading, setIsRecordingLoading] = useState(false);

  const handleRecordBtn = () => {
    const meetingId = data?.meeting_id;
    if (!meetingId || isRecordingLoading) return;

    setIsRecordingLoading(true);
    if (isRecording) {
      stopRecordingApiCall(
        meetingId,
        () => { setIsRecording(false); setIsRecordingLoading(false); },
        () => { notifyError(t('call_modal.recording_stop_failed')); setIsRecordingLoading(false); }
      );
    } else {
      startRecordingApiCall(
        meetingId,
        () => { setIsRecording(true); setIsRecordingLoading(false); },
        () => { notifyError(t('call_modal.recording_start_failed')); setIsRecordingLoading(false); }
      );
    }
  };

  const handleToggleAiSummary = (enabled) => {
    const meetingId = data?.meeting_id;
    if (!meetingId) return;

    updateMeetingCallSettingsApiCall(
      meetingId,
      { ai_summary_enabled: enabled },
      (response) => {
        setAiSummaryEnabled(response.data.ai_summary_enabled);
      },
      () => notifyError(t('call_modal.settings_update_failed'))
    );
  };

  const handleToggleTranscriptExport = (enabled) => {
    const meetingId = data?.meeting_id;
    if (!meetingId) return;

    updateMeetingCallSettingsApiCall(
      meetingId,
      { transcript_export_enabled: enabled },
      (response) => {
        setTranscriptExportEnabled(response.data.transcript_export_enabled);
      },
      () => notifyError(t('call_modal.settings_update_failed'))
    );
  };

  const processMessage = useCallback((msgData) => {
    if (callJoinedTimestampRef.current && msgData.created_at) {
      const messageCreatedAt = new Date(msgData.created_at).getTime();
      const CLOCK_SKEW_BUFFER_MS = 5000;
      if (messageCreatedAt < callJoinedTimestampRef.current - CLOCK_SKEW_BUFFER_MS) {
        seenMessageIds.current.add(msgData.id);
        return;
      }
    }

    if (leftPanelViewRef.current !== "chat" && 
        msgData.user_id !== user?.id && 
        !seenMessageIds.current.has(msgData.id)) {
      
      seenMessageIds.current.add(msgData.id);
      setUnreadCallChatCount((prev) => prev + 1);
      
      const senderName = msgData.user?.name || tRef.current("general.someone");
      const senderAvatar = msgData.user?.profile_image || null;
      
      const translation = msgData.message_translations?.find(
        trans => trans.user_id === user.id
      );
      const messageText = translation?.translation || msgData.original_message;
      const messagePreview = messageText?.substring(0, 100) || "";
      
      notifyCallChatMessageRef.current(
        senderName,
        messagePreview,
        () => {
          setLeftPanelView("chat");
          setUnreadCallChatCount(0);
        },
        senderAvatar
      );
    } else if (msgData.user_id !== user?.id) {
      seenMessageIds.current.add(msgData.id);
    }
  }, [user?.id]);

  useEffect(() => {
    if (
      callingSoundHowl.playing() === true &&
      remoteNonAgentParticipants.length > 0
    ) {
      callingSoundHowl.stop();
      connectedSoundHowl.play();
    }
    avLog.debug("remoteNonAgentParticipants", remoteNonAgentParticipants);
  }, [remoteNonAgentParticipants]);

  useEffect(() => {
    if (callStartTime) return;
    if (isConference || remoteNonAgentParticipants.length > 0) {
      setCallStartTime(Date.now());
    }
  }, [isConference, remoteNonAgentParticipants.length, callStartTime]);

  useEffect(() => {
    return () => {
      if (room) {
        room.disconnect();
        callingSoundHowl.stop();
        // This might not be needed leave it commented for testing
        // resetCall();
      }
    };
  }, []);

  useEffect(() => {
    if (speakStatus !== previousSpeakStatus && speakStatus === SPEAKING) {
      beepSoundHowl.play();
    }
    setPreviousSpeakStatus(speakStatus);
  }, [speakStatus]);

  useEffect(() => {
    if (!data) {
      return;
    }
    verifyCallType(data);
  }, [data]);

  useEffect(() => {
    if (!data) {
      return;
    }
    mapCallParticipants(data);
  }, [data, user]);

  useEffect(() => {
    const handleReconnecting = () => {
      avLog.info("[EMOTII-DBG] LiveKit RoomEvent.Reconnecting — resetting hasSeenRemoteParticipants");
      hasSeenRemoteParticipantsRef.current = false;
    };
    const handleReconnected = () => {
      avLog.info("[EMOTII-DBG] LiveKit RoomEvent.Reconnected");
    };
    const handleDisconnected = (reason) => {
      avLog.info("[EMOTII-DBG] LiveKit RoomEvent.Disconnected — reason:", reason);
    };
    const handleParticipantConnected = (participant) => {
      avLog.debug("[EMOTII-DBG] LiveKit ParticipantConnected:", participant.identity);
    };
    const handleParticipantDisconnected = (participant) => {
      avLog.debug("[EMOTII-DBG] LiveKit ParticipantDisconnected:", participant.identity, "reason:", participant.disconnectReason);
    };
    const handleTrackUnpublished = (publication, participant) => {
      avLog.debug("[EMOTII-DBG] LiveKit TrackUnpublished:", participant.identity, "track:", publication.kind, publication.source);
    };
    room.on(RoomEvent.Reconnecting, handleReconnecting);
    room.on(RoomEvent.Reconnected, handleReconnected);
    room.on(RoomEvent.Disconnected, handleDisconnected);
    room.on(RoomEvent.ParticipantConnected, handleParticipantConnected);
    room.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
    room.on(RoomEvent.TrackUnpublished, handleTrackUnpublished);
    return () => {
      room.off(RoomEvent.Reconnecting, handleReconnecting);
      room.off(RoomEvent.Reconnected, handleReconnected);
      room.off(RoomEvent.Disconnected, handleDisconnected);
      room.off(RoomEvent.ParticipantConnected, handleParticipantConnected);
      room.off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected);
      room.off(RoomEvent.TrackUnpublished, handleTrackUnpublished);
    };
  }, [room]);

  useEffect(() => {
    avLog.debug("[EMOTII-DBG] translationType changed to:", translationType, "— resetting hasSeenRemoteParticipants");
    hasSeenRemoteParticipantsRef.current = false;
  }, [translationType]);

  useEffect(() => {
    const isConferenceHost = [
      CONFERENCE_ROLE_ORGANIZER,
      CONFERENCE_ROLE_PRESENTER,
    ].includes(data?.conference_role);
    if (!isConference || !isConferenceHost) {
      return;
    }

    const handleReconnecting = () => {
      micPublishAttemptedRef.current = false;
    };

    const handleReconnected = () => {
      const participant = localParticipant.localParticipant;
      if (participant && isCurrentSpeaker) {
        participant
          .setMicrophoneEnabled(true)
          .then((publication) => {
            if (micEnabledRef.current) {
              (publication?.track ?? participant.getTrackPublication(Track.Source.Microphone)?.track)?.mute();
            }
          })
          .catch((err) => {
            avLog.warn("🔊 [Conference audio] mic re-publish on reconnect failed", err);
          });
      }
      if (data?.uuid && user?.id) {
        avLog.info("🔊 [Conference audio] requesting speaker agent resync after reconnect");
        sendConferenceSpeakerResync(data.uuid, user.id);
      }
    };

    room.on(RoomEvent.Reconnecting, handleReconnecting);
    room.on(RoomEvent.Reconnected, handleReconnected);
    return () => {
      room.off(RoomEvent.Reconnecting, handleReconnecting);
      room.off(RoomEvent.Reconnected, handleReconnected);
    };
  }, [
    room,
    isConference,
    data?.conference_role,
    isCurrentSpeaker,
    localParticipant,
    data?.uuid,
    user?.id,
    sendConferenceSpeakerResync,
  ]);

  useEffect(() => {
    const snap = {
      remoteNonAgentCount: remoteNonAgentParticipants.length,
      hasSeen: hasSeenRemoteParticipantsRef.current,
      roomState: room.state,
      otherParticipantsCount: data.other_participants?.length,
      translationType,
      isConference: !!data.conference_id,
      hasMeeting: !!currentMeetingRef.current,
    };
    avLog.debug("[EMOTII-DBG] auto-disconnect check", snap);
    if (remoteNonAgentParticipants.length > 0) {
      hasSeenRemoteParticipantsRef.current = true;
      return;
    }
    if (currentMeetingRef.current || data.conference_id) {
      return;
    }
    if (data.other_participants.length >= 1 && room.state === "connected" && hasSeenRemoteParticipantsRef.current) {
      avLog.warn("[EMOTII-DBG] ⚠️ auto-disconnect FIRING — calling room.disconnect() + resetCall()");
      room.disconnect();
      callingSoundHowl.stop();
      resetCall();
    }
  }, [remoteNonAgentParticipants.length, data.conference_id, translationType]);

  useEffect(() => {
    publishedTracksObserver();
  }, [publishedTracks]);

  useEffect(() => {
    if (
      selectedAudioInput &&
      selectedAudioInput !== audioInputDevice.defaultDevice &&
      audioInputDevice.devices.find((d) => d.deviceId === selectedAudioInput)
    ) {
      audioInputDevice.setActiveMediaDevice(selectedAudioInput);
    }

    if (
      selectedVideoInput &&
      selectedVideoInput !== videoInputDevice.defaultDevice &&
      videoInputDevice.devices.find((d) => d.deviceId === selectedVideoInput)
    ) {
      videoInputDevice.setActiveMediaDevice(selectedVideoInput);
    }

    if (
      selectedAudioOutput &&
      selectedAudioOutput !== audioOutputDevice.defaultDevice &&
      audioOutputDevice.devices.find((d) => d.deviceId === selectedAudioOutput)
    ) {
      audioOutputDevice.setActiveMediaDevice(selectedAudioOutput);
    }
  }, [
    audioInputDevice.devices,
    videoInputDevice.devices,
    audioOutputDevice.devices,
    audioInputDevice.activeDeviceId,
    videoInputDevice.activeDeviceId,
    audioOutputDevice.activeDeviceId,
    selectedAudioInput,
    selectedVideoInput,
    selectedAudioOutput,
  ]);

  useEffect(() => {
    if (!user || !user.id) return;
    if (shouldBypassMicPermissions) {
      return;
    }

    const threshold = 30;
    let animationFrameId;
    let resetTimeout;
    let cancelled = false;
    let speakingWhileMuted = false;

    const setupMicDetection = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        micStreamRef.current = stream;

        if (
          !audioContextRef.current ||
          audioContextRef.current.state === "closed"
        ) {
          audioContextRef.current = new AudioContext();
        }

        const source = audioContextRef.current.createMediaStreamSource(stream);
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 256;
        analyserRef.current.smoothingTimeConstant = 0.4;
        source.connect(analyserRef.current);
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

        const checkSpeakingWhileMuted = () => {
          if (cancelled) return;
          analyserRef.current.getByteFrequencyData(dataArray);
          const volume =
            dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
          const volumeAboveThreshold = volume > threshold;

          if (volumeAboveThreshold && micEnabledRef.current) {
            speakingWhileMutedFrameCount.current = Math.min(
              20,
              speakingWhileMutedFrameCount.current + 1
            );
            if (resetTimeout) clearTimeout(resetTimeout);
            resetTimeout = null;
          } else {
            speakingWhileMutedFrameCount.current = Math.max(
              0,
              speakingWhileMutedFrameCount.current - 2
            );
            if (!resetTimeout) {
              resetTimeout = setTimeout(() => {
                resetTimeout = null;
                if (speakingWhileMutedFrameCount.current < 5) {
                  speakingWhileMuted = false;
                  setIsSpeakingWhileMuted(false);
                }
              }, 400);
            }
          }

          if (speakingWhileMutedFrameCount.current > 12 && !speakingWhileMuted) {
            speakingWhileMuted = true;
            setIsSpeakingWhileMuted(true);
          }

          animationFrameId = requestAnimationFrame(checkSpeakingWhileMuted);
        };

        checkSpeakingWhileMuted();
      } catch (err) {
        console.error("Mic detection error:", err);
      }
    };

    setupMicDetection();

    return () => {
      cancelled = true;
      cancelAnimationFrame(animationFrameId);
      if (resetTimeout) clearTimeout(resetTimeout);
      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        audioContextRef.current.close().catch(() => {});
      }
      micStreamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [shouldBypassMicPermissions, user]);

  useEffect(() => {
    if (micEnabled && isSpeakingWhileMuted) {
      const now = Date.now();
      const timeSinceLastNotify = now - lastNotifiedAt.current;

      if (timeSinceLastNotify > 20000) {
        // 20 seconds
        notifyInfo(t('call_modal.you_are_muted'));
        lastNotifiedAt.current = now;
      }
    }
  }, [micEnabled, isSpeakingWhileMuted]);

  useEffect(() => {
    setCurrentSpeakerIsActive(isLocalSpeaking);
  }, [isLocalSpeaking, setCurrentSpeakerIsActive]);

  // To re-enable Krisp noise cancellation, set KRISP_ENABLED = true at the top of this file
  useEffect(() => {
    if (!KRISP_ENABLED) return;
    if (setNoiseFilterEnabled && !isNoiseFilterEnabled && !isNoiseFilterPending) {
      setNoiseFilterEnabled(true);
    }
  }, [setNoiseFilterEnabled, isNoiseFilterEnabled, isNoiseFilterPending]);

  const remoteNoiseFilterEnabledRef = useRef(remoteNoiseFilterEnabled);
  const remoteNoiseFilterProcessorsRef = useRef(new Map());

  useEffect(() => {
    remoteNoiseFilterEnabledRef.current = remoteNoiseFilterEnabled;
  }, [remoteNoiseFilterEnabled]);

  const isAgentOrTtsAudioTrack = useCallback(
    (participant, publication) => {
      const identity = participant?.identity || "";
      const trackName = getPublicationTrackName(publication);
      return (
        identity.startsWith(AGENT_IDENTITY_PREFIX) ||
        isConferenceAgentIdentity(identity) ||
        isConferenceTtsTrackName(trackName)
      );
    },
    []
  );

  const attachRemoteNoiseFilter = useCallback(async (track, publication, participant) => {
    if (
      !track ||
      !publication ||
      !isAudioPublication(publication) ||
      publication.source !== Track.Source.Microphone ||
      isAgentOrTtsAudioTrack(participant, publication) ||
      remoteNoiseFilterProcessorsRef.current.has(publication.trackSid)
    ) {
      return;
    }
    try {
      const { KrispNoiseFilter, isKrispNoiseFilterSupported } = await import(
        "@livekit/krisp-noise-filter"
      );
      if (!isKrispNoiseFilterSupported()) return;
      const processor = KrispNoiseFilter();
      await track.setProcessor(processor);
      remoteNoiseFilterProcessorsRef.current.set(publication.trackSid, processor);
      const shouldBeEnabled = remoteNoiseFilterEnabledRef.current && !publication.isMuted;
      await processor.setEnabled(shouldBeEnabled);
    } catch (err) {
      avLog.error("[CallView] failed to attach remote noise filter", err);
    }
  }, [isAgentOrTtsAudioTrack]);

  const detachRemoteNoiseFilter = useCallback(async (publication) => {
    const processor = remoteNoiseFilterProcessorsRef.current.get(publication?.trackSid);
    if (!processor) return;
    remoteNoiseFilterProcessorsRef.current.delete(publication.trackSid);
    try {
      await publication.track?.stopProcessor();
    } catch (err) {
      avLog.error("[CallView] failed to detach remote noise filter", err);
    }
  }, []);

  useEffect(() => {
    if (!REMOTE_NOISE_FILTER_ENABLED || !room) return;

    const handleRemoteTrackSubscribed = (track, publication, participant) => {
      if (remoteNoiseFilterEnabledRef.current) {
        attachRemoteNoiseFilter(track, publication, participant);
      }
    };

    const handleRemoteTrackUnsubscribed = (_track, publication) => {
      detachRemoteNoiseFilter(publication);
    };

    const handleRemoteTrackMuted = (publication) => {
      const processor = remoteNoiseFilterProcessorsRef.current.get(publication.trackSid);
      processor?.setEnabled(false).catch(() => {});
    };

    const handleRemoteTrackUnmuted = (publication) => {
      const processor = remoteNoiseFilterProcessorsRef.current.get(publication.trackSid);
      if (processor && remoteNoiseFilterEnabledRef.current) {
        processor.setEnabled(true).catch(() => {});
      }
    };

    room.on(RoomEvent.TrackSubscribed, handleRemoteTrackSubscribed);
    room.on(RoomEvent.TrackUnsubscribed, handleRemoteTrackUnsubscribed);
    room.on(RoomEvent.TrackMuted, handleRemoteTrackMuted);
    room.on(RoomEvent.TrackUnmuted, handleRemoteTrackUnmuted);

    return () => {
      room.off(RoomEvent.TrackSubscribed, handleRemoteTrackSubscribed);
      room.off(RoomEvent.TrackUnsubscribed, handleRemoteTrackUnsubscribed);
      room.off(RoomEvent.TrackMuted, handleRemoteTrackMuted);
      room.off(RoomEvent.TrackUnmuted, handleRemoteTrackUnmuted);
    };
  }, [room, attachRemoteNoiseFilter, detachRemoteNoiseFilter]);

  useEffect(() => {
    if (!REMOTE_NOISE_FILTER_ENABLED || !room) return;

    room.remoteParticipants.forEach((participant) => {
      participant.audioTrackPublications.forEach((publication) => {
        if (publication.source !== Track.Source.Microphone || !publication.track) return;
        if (remoteNoiseFilterEnabled) {
          attachRemoteNoiseFilter(publication.track, publication, participant);
        } else {
          const processor = remoteNoiseFilterProcessorsRef.current.get(publication.trackSid);
          processor?.setEnabled(false).catch(() => {});
        }
      });
    });
  }, [remoteNoiseFilterEnabled, room, attachRemoteNoiseFilter]);

  useEffect(() => {
    if (!REMOTE_NOISE_FILTER_ENABLED) return;
    const processors = remoteNoiseFilterProcessorsRef.current;
    return () => {
      processors.forEach((processor) => {
        processor?.destroy?.().catch(() => {});
      });
      processors.clear();
    };
  }, []);

  useEffect(() => {
    leftPanelViewRef.current = leftPanelView;
  }, [leftPanelView]);

  useEffect(() => {
    translationTypeRef.current = translationType;
  }, [translationType]);

  const showControlBar = useCallback(() => {
    setControlBarVisible(true);
    if (translationTypeRef.current !== DISABLED_TRANSLATION) return;
    if (controlBarHideTimerRef.current) clearTimeout(controlBarHideTimerRef.current);
    controlBarHideTimerRef.current = setTimeout(() => setControlBarVisible(false), 3000);
  }, []);

  useEffect(() => {
    const shouldAutoHide = participantIsSharingScreen && translationType === DISABLED_TRANSLATION;
    if (shouldAutoHide) {
      showControlBar();
    } else {
      setControlBarVisible(true);
      if (controlBarHideTimerRef.current) clearTimeout(controlBarHideTimerRef.current);
    }
    return () => {
      if (controlBarHideTimerRef.current) clearTimeout(controlBarHideTimerRef.current);
    };
  }, [participantIsSharingScreen, translationType]);


  useEffect(() => {
    if (!SCREENSHARE_OVERLAY_AUTOTOGGLE) return;
    if (participantIsSharingScreen && translationEnabled && translationType !== DISABLED_TRANSLATION) {
      setRightPanelAsOverlay(true);
      setRightPanelCollapsed(false);
    } else {
      setRightPanelAsOverlay(false);
    }
  }, [participantIsSharingScreen, translationEnabled, translationType]);


  useEffect(() => {
    if (!data?.id || !user?.id) return;

    const conversationObserver = {
      channelName: "ConversationsChannel",
      update: (rawData) => {
        const wsData = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
        
        const callMatchId = wsData.call_id || wsData.conference_call_id;
        if (callMatchId === data.id && wsData.id) {
          callChatConversationIdRef.current = wsData.id;
          setCallChatConversation({ ...wsData });
          setIsCheckingConversation(false);

          const pending = pendingMessagesRef.current.filter(
            m => m.conversation_id === wsData.id
          );
          pendingMessagesRef.current = [];
          pending.forEach(m => processMessageRef.current(m));
          
          if (leftPanelViewRef.current !== "chat") {
            const unreadCount = wsData.unread_messages_ids?.length || 0;
            setUnreadCallChatCount(unreadCount);
          }
          return;
        }
        if (wsData.id === callChatConversationIdRef.current) {
          if (wsData.other_participants) {
            setCallChatConversation({ ...wsData });
          }
          
          if (leftPanelViewRef.current !== "chat") {
            const unreadCount = wsData.unread_messages_ids?.length || 0;
            setUnreadCallChatCount(unreadCount);
          } else {
            setUnreadCallChatCount(0);
          }
        }
      },
    };

    add_observer(conversationObserver);

    return () => {
      remove_observer(conversationObserver);
    };
  }, [data?.id, user?.id]);

  useEffect(() => {
    notifyCallChatMessageRef.current = notifyCallChatMessage;
  }, [notifyCallChatMessage]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(() => {
    processMessageRef.current = processMessage;
  }, [processMessage]);

  useEffect(() => {
    if (!user?.id || !data?.id) return;

    const messageObserver = {
      channelName: "MessagesChannel",
      update: (rawData) => {
        const msgData = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;

        const conversationId = msgData.conversation_id;
        
        if (conversationId === callChatConversationIdRef.current) {
          processMessageRef.current(msgData);
          return;
        }

        if (!callChatConversationIdRef.current && conversationId) {
          pendingMessagesRef.current.push(msgData);
        }
      },
    };
    add_observer(messageObserver);

    return () => {
      remove_observer(messageObserver);
    };
  }, [user?.id, data?.id]);

  useEffect(() => {
    if (!data?.conference_id || !user?.id) return;

    const pollObserver = {
      channelName: "ConferenceCallChannel",
      update: (rawData) => {
        try {
          const pollData = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;

          if (pollData.message_type === "poll_launched") {
            if (isConferenceAttendee && pollsAllowed && leftPanelViewRef.current !== "polls") {
              setActivePollBadge(true);
              
              if (pollData.poll?.question_text) {
                notifyCallPoll(
                  pollData.poll.question_text,
                  () => {
                    setLeftPanelView("polls");
                    setActivePollBadge(false);
                  }
                );
              }
            }
          }

          if (pollData.message_type === "poll_ended") {
            setActivePollBadge(false);
          }
        } catch (error) {
          // Silent error handling
        }
      },
    };

    add_observer(pollObserver);

    return () => {
      remove_observer(pollObserver);
    };
  }, [data?.conference_id, user?.id, isConferenceAttendee, notifyCallPoll]);

  useEffect(() => {
    if (!data?.conference_id || !user?.id) return;

    const chatToggleObserver = {
      channelName: "ConferenceCallChannel",
      update: (rawData) => {
        try {
          const chatToggleData = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;

          if (chatToggleData.message_type === "chat_toggled") {
            setChatAllowed(!!chatToggleData.allow_chat);
            if (!chatToggleData.allow_chat && leftPanelViewRef.current === "chat") {
              setLeftPanelView(null);
            }
          }
        } catch (error) {
          // Silent error handling
        }
      },
    };

    add_observer(chatToggleObserver);

    return () => {
      remove_observer(chatToggleObserver);
    };
  }, [data?.conference_id, user?.id]);

  const visibleRaisedHands = isConferenceAttendee ? [] : raisedHands;
  const isHandRaised = visibleRaisedHands.some((hand) => String(hand.user_id) === String(user?.id));

  const controlBarElement = (
    <ControlBarOverlay
      $screenSharing={participantIsSharingScreen != null}
      $visible={controlBarVisible}
    >
      <ControlBar
        handleCloseCallBtn={handleDisconect}
        handleVideoBtn={handleVideoBtn}
        videoEnabled={videoEnabled}
        handleMuteBtn={handleMuteBtn}
        translaionType={translationType}
        micEnabled={micEnabled}
        chatAllowed={chatAllowed}
        pollsAllowed={pollsAllowed}
        micAllowed={micAllowed}
        cameraAllowed={cameraAllowed}
        isAttendee={isConferenceAttendee}
        handRaised={isHandRaised}
        handleRaiseHandBtn={isHandRaised ? handleLowerHand : handleRaiseHand}
        hansleParticipantsBtn={handleToggleParticipants}
        handleChatBtn={handleToggleChat}
        handlePollsBtn={handleTogglePolls}
        isParticipantsOpen={leftPanelView === "participants"}
        isChatOpen={leftPanelView === "chat"}
        isPollsOpen={leftPanelView === "polls"}
        unreadChatCount={unreadCallChatCount}
        activePollBadge={activePollBadge}
        grid={grid}
        isMeeting={Object.hasOwn(data, 'meeting_id') && data.meeting_id != null}
        isConference={isConference}
        showChatToggleMenu={canManageChatToggle}
        chatEnabled={chatAllowed}
        onToggleChat={handleToggleChatSetting}
        handleShareScreenBtn={handleShareScreenBtn}
        shareScreenAllowed={shareScreenAllowed}
        handleOpenOptionModal={handleOpenOptionModal}
        screenSharing={screenSharing}
        handleSwitchGrid={handleSwitchGrid}
        activeCallRef={activeCallRef}
        handleRecordBtn={handleRecordBtn}
        isRecording={isRecording}
        isRecordingLoading={isRecordingLoading}
        meetingId={data?.meeting_id}
        aiSummaryEnabled={aiSummaryEnabled}
        transcriptExportEnabled={transcriptExportEnabled}
        onToggleAiSummary={handleToggleAiSummary}
        onToggleTranscriptExport={handleToggleTranscriptExport}
      />
    </ControlBarOverlay>
  );

  return (
    <RaisedHandsProvider raisedHands={visibleRaisedHands}>
    <CallViewWrapper>
      {isPickingShareScreen && (
        <ShareScreenPicker
          onSelect={handleShareDesktopWindowSelected}
          onClose={() => setIsPickingShareScreen(false)}
        />
      )}

      {isOptionModalOpen && (
        <CallOptionModal
          onClose={handleCloseOptionModal}
          audioInputDevice={audioInputDevice}
          videoInputDevice={videoInputDevice}
          audioOutputDevice={audioOutputDevice}
          micAllowed={micAllowed}
          cameraAllowed={cameraAllowed}
          isAttendee={isConferenceAttendee}
          backgroundType={backgroundType}
          setBackgoundType={setBackgoundType}
          handleVirtualBackground={handleVirtualBackground}
          setSelectedAudioInput={setSelectedAudioInput}
          setSelectedAudioOutput={setSelectedAudioOutput}
          setSelectedVideoInput={setSelectedVideoInput}
          onLanguageChange={handleLanguageChange}
          isConference={isConference}
          remoteNoiseFilterVisible={REMOTE_NOISE_FILTER_ENABLED}
          remoteNoiseFilterEnabled={remoteNoiseFilterEnabled}
          setRemoteNoiseFilterEnabled={(enabled) => {
            LocalStorage.setRemoteNoiseFilterEnabled(enabled);
            setRemoteNoiseFilterEnabled(enabled);
          }}
        />
      )}
      <AgentAwareAudioRenderer
        emotiiVoice={emotiiVoice}
        userId={user?.id}
        isSpeaking={isLocalSpeaking}
        useRealtimeApi={useRealtimeApi}
        useLiveKitAgentAudio={useLiveKitAgentAudio}
        nonAgentVolume={finalIncomingVolume}
        applyVolumes={applyVolumesToParticipants}
      />

      {remoteNonAgentParticipants.length === 0 && !isConference && (
        <ConnectingView
          callInfo={data}
          handleCloseCallBtn={handleDisconect}
          sendCloseCallMessage={sendCloseCallMessage}
          resetCall={resetCall}
        />
      )}

      {(remoteNonAgentParticipants.length > 0 || isConference) && (
        <MainContainer>
          <LeftSection>
            <CallHeader
              callDetails={data}
              handleSwitchGrid={handleSwitchGrid}
              translationType={translationType}
              handleSelectTranslationType={handleSelectTranslationType}
              translationModeChangePending={conferenceTranslationModeChangePending}
              translationEnabled={
                translationEnabled || participantIsSharingScreen
              }
              callStartTime={callStartTime}
              showEndConferenceBtn={canEndConference}
              handleEndConferenceBtn={handleEndConference}
              isRecording={isRecording}
              aiSummaryEnabled={aiSummaryEnabled}
              transcriptExportEnabled={transcriptExportEnabled}
              canChangeTranslationType={canChangeTranslationType}
              raisedHands={visibleRaisedHands}
          />
            <Content>
              {leftPanelView === "chat" && (
                <CallChatPanel
                  callInfo={activeCallRef.current?.details || data}
                  onClose={handleCloseLeftPanel}
                  activeCallRef={activeCallRef}
                  existingConversation={callChatConversation}
                  onConversationCreated={(conv) => {
                    setCallChatConversation(conv);
                  }}
                  onMessagesRead={() => setUnreadCallChatCount(0)}
                />
              )}

              {leftPanelView === "participants" && (
                <Participants
                  callInfo={data}
                  setLoading={setLoading}
                  activeCallRef={activeCallRef}
                  setSelectedCall={setSelectedCall}
                  callParticipants={callParticipants}
                  hansleParticipantsBtn={handleCloseLeftPanel}
                  isConferenceHost={!isConferenceAttendee && !!data?.conference_id}
                  addObserver={add_observer}
                  removeObserver={remove_observer}
                  speakersQueue={speakersQueue}
                  translationType={translationType}
                  canReorderSpeakers={canReorderSpeakers}
                  onReorderSpeakers={handleReorderSpeakers}
                />
              )}

              {leftPanelView === "polls" && (
                <PollPanel
                  conferenceId={data?.conference_id}
                  isOrganizer={!isConferenceAttendee && data?.created_by?.id === user?.id}
                  isPresenter={!isConferenceAttendee && data?.created_by?.id !== user?.id}
                  onClose={handleCloseLeftPanel}
                  addObserver={add_observer}
                  removeObserver={remove_observer}
                />
              )}

              <ConversationCore onMouseMove={participantIsSharingScreen ? showControlBar : undefined}>
                {remoteNonAgentParticipants.length === 0 && isConference && (
                  <LocalOnlyContainer ref={containerRef}>
                    <ParticipantComponent
                      key={"local"}
                      localParticipant={true}
                      participant={localParticipant.localParticipant}
                      callInfo={data}
                      grid={false}
                    />
                  </LocalOnlyContainer>
                )}

                {remoteNonAgentParticipants.length === 1 &&
                  participantIsSharingScreen === null &&
                  displaySingleCall()}
                {remoteNonAgentParticipants.length > 1 &&
                  !translationEnabled &&
                  !participantIsSharingScreen && (
                    <ParticipantsWrapper
                      callInfo={data}
                      participants={[localParticipant.localParticipant].concat(
                        remoteNonAgentParticipants
                      )}
                      grid={grid}
                      participantIsSharingScreen={participantIsSharingScreen}
                      speakersQueue={speakersQueue}
                    />
                  )}

                {remoteNonAgentParticipants.length > 1 &&
                  translationEnabled &&
                  !participantIsSharingScreen && (
                    <ParticipantsWrapperTranslationGrid
                      callInfo={data}
                      grid={grid}
                      participants={[localParticipant.localParticipant].concat(
                        remoteNonAgentParticipants
                      )}
                      speakersQueue={speakersQueue}
                    />
                  )}

                {remoteNonAgentParticipants.length > 0 &&
                  participantIsSharingScreen != null && (
                    <ScreenSharingGrid
                      callInfo={data}
                      participants={[localParticipant.localParticipant].concat(
                        remoteNonAgentParticipants
                      )}
                      screenSharingParticipant={participantIsSharingScreen}
                      screenSharingParticipants={screenSharingParticipantsList}
                      handleSelectScreenShareParticipant={
                        handleSelectScreenShareParticipant
                      }
                      speakersQueue={speakersQueue}
                      manuallySelectedScreenShare={manuallySelectedScreenShare}
                      cameraStripCollapsed={cameraStripCollapsed}
                      onToggleCameraStrip={() => setCameraStripCollapsed((v) => !v)}
                      controlBar={controlBarElement}
                    />
                  )}

                {participantIsSharingScreen == null && controlBarElement}
              </ConversationCore>
            </Content>
          </LeftSection>

          {translationEnabled && translationType !== DISABLED_TRANSLATION && (
            <>
              {RIGHT_PANEL_OVERLAY_ENABLED && rightPanelCollapsed && (
                <RightPanelExpandTab onClick={() => setRightPanelCollapsed(false)}>
                  <BackArrowIcon />
                </RightPanelExpandTab>
              )}
              <RightSection
                $overlay={RIGHT_PANEL_OVERLAY_ENABLED && rightPanelAsOverlay}
                $collapsed={RIGHT_PANEL_OVERLAY_ENABLED && rightPanelCollapsed}
              >
                {RIGHT_PANEL_OVERLAY_ENABLED && (
                  <RightPanelControls>
                    <PanelControlBtn
                      onClick={() => setRightPanelAsOverlay((v) => !v)}
                      $active={rightPanelAsOverlay}
                      title={rightPanelAsOverlay ? "Dock panel" : "Float panel"}
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <rect x="0.75" y="0.75" width="8.5" height="8.5" rx="1.25" stroke="currentColor" strokeWidth="1.5"/>
                        <rect x="5" y="5" width="8.25" height="8.25" rx="1.25" fill="currentColor" fillOpacity="0.25" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                    </PanelControlBtn>
                    <PanelControlBtn
                      onClick={() => setRightPanelCollapsed(true)}
                      title="Collapse panel"
                    >
                      <ForwardArrowIcon />
                    </PanelControlBtn>
                  </RightPanelControls>
                )}
                <SpeakingTranslatorLiveKit
                  translationType={translationType}
                  micEnabled={micEnabled}
                  speechRecongnitionMessages={speechRecongnitionMessages}
                  speakStatus={speakStatus}
                  handleSendCancelSpeakMessage={handleSendCancelSpeakMessage}
                  setSpeakStatus={setSpeakStatus}
                  handleRequestSpeakMessage={handleRequestSpeakMessage}
                  speakersQueue={speakersQueue}
                  muteMicrophone={muteMicrophone}
                  unmuteMicrophone={unmuteMicrophone}
                  micEnabledRef={micEnabledRef}
                  setEmotiiVoice={setEmotiiVoice}
                  emotiiVoice={emotiiVoice}
                  useRealtimeApi={useRealtimeApi}
                  realtimeTranslations={realtimeTranslations}
                  canRequestSpeak={!isConferenceAttendee}
                  requestSpeakPayload={requestSpeakPayload}
                  onIncomingVolumeChange={handleIncomingVolumeChange}
                  onEmotiiVolumeChange={handleEmotiiVolumeChange}
                  activeCallRef={activeCallRef}
                  isConference={isConference}
                  isConferenceAttendee={isConferenceAttendee}
                  conferenceTtsPlaybackRate={conferenceTtsPlaybackRate}
                  onConferenceTtsSpeedChange={handleConferencePlaybackSpeedChange}
                  incomingVolume={incomingVolume}
                  dimEnabled={dimEnabled}
                  emotiiVolume={emotiiVolume}
                  isLeavingCallRef={isLeavingCallRef}
                  isOverlay={RIGHT_PANEL_OVERLAY_ENABLED && rightPanelAsOverlay}
                />
              </RightSection>
            </>
          )}
        </MainContainer>
      )}
    </CallViewWrapper>
    </RaisedHandsProvider>
  );
};

export default CallView;

const CallViewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.menu.background};
`;

const MainContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  height: 100%;
  overflow: hidden;
`;

const RightSection = styled.div`
  display: ${({ $collapsed }) => ($collapsed ? "none" : "flex")};
  flex-direction: column;
  width: 450px;
  min-width: 450px;
  max-width: 450px;
  height: 100%;
  overflow: hidden;

  ${({ $overlay }) =>
    $overlay &&
    `
    position: absolute;
    right: 0;
    top: 0;
    z-index: 20;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    background-color: rgba(0,0,0, 0.2);
    border-left: 1px solid rgba(255, 255, 255, 0.08);
  `}
`;

const RightPanelControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  padding: 6px 8px 0;
  flex-shrink: 0;
`;

const PanelControlBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: ${({ $active }) => ($active ? "rgba(255,255,255,0.18)" : "transparent")};
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.9);
  }
`;

const RightPanelExpandTab = styled.button`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 21;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 56px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-right: none;
  border-radius: 8px 0 0 8px;
  cursor: pointer;
  padding: 0;
  color: rgba(255, 255, 255, 0.6);
  transition: background 0.15s, color 0.15s;

  svg {
    width: 12px;
    height: 12px;
    transform: rotate(180deg);
  }

  &:hover {
    background: rgba(255, 255, 255, 0.16);
    color: rgba(255, 255, 255, 0.9);
  }
`;


const Content = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  gap: 20px;
  padding: 0 20px;
`;

const ConversationCore = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  min-width: 340px;
`;

const ControlBarOverlay = styled.div`
  width: 100%;
  margin-top: auto;

  ${({ $screenSharing, $visible }) =>
    $screenSharing &&
    `
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 10;
    margin-top: 0;
    opacity: ${$visible ? "1" : "0"};
    transition: opacity 0.4s ease;
    pointer-events: ${$visible ? "auto" : "none"};
  `}
`;

const LocalOnlyContainer = styled.div`
  display: flex;
  width: 100%;
  height: calc(100% - 80px);
  position: relative;
`;

const LocalOnlyMessage = styled.p`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 100px;
  margin: 0;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;
